import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { updateDoc, doc, getDoc, getDocs, collection, query, where, limit, onSnapshot, increment, serverTimestamp, addDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../context/NotificationContext';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { ProfileData } from '../types';
import { getUniqueStates, getDistrictsForState } from '../data/stateDistrictData';
import {
  UserIcon,
  CreditCardIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
  CogIcon,
  QuestionMarkCircleIcon,
  ChevronRightIcon,
  CameraIcon,
  TrophyIcon,
  MapPinIcon,
  PhoneIcon,
  BuildingStorefrontIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import DealerCodeModal from '../components/modals/DealerCodeModal';
import { useTranslation } from 'react-i18next';
import { userAnalytics } from '../services/userAnalytics';

// Custom hook for responsive max-width
const useResponsiveMaxWidth = () => {
  const [maxWidth, setMaxWidth] = useState('480px');

  useEffect(() => {
    const updateMaxWidth = () => {
      const width = window.innerWidth;
      if (width < 375) {
        setMaxWidth('100%');
      } else if (width < 768) {
        setMaxWidth('min(480px, calc(100% - 32px))');
      } else if (width < 1024) {
        setMaxWidth('min(600px, calc(100% - 48px))');
      } else {
        setMaxWidth('min(800px, calc(100% - 64px))');
      }
    };

    updateMaxWidth();
    window.addEventListener('resize', updateMaxWidth);
    return () => window.removeEventListener('resize', updateMaxWidth);
  }, []);

  return maxWidth;
};

// Move styles to CSS classes for better performance
const Profile: React.FC = React.memo(() => {
  const maxWidth = useResponsiveMaxWidth();
  // ... existing code ...

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSharePreview, setShowSharePreview] = useState(false);
  const [shareImageUrl, setShareImageUrl] = useState<string | null>(null);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [realScanCount, setRealScanCount] = useState<number | null>(null);
  const [showKycModal, setShowKycModal] = useState(false);
  const [editFormData, setEditFormData] = useState<ProfileData>({
    name: '',
    phone: '',
    upiId: '',
    state: '',
    district: '',
    city: '',
    pinCode: '',
    photoFile: null,
    address: '',
    age: undefined,
    accountType: 'Painter'
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    paymentMethod: 'bank',
    upiId: '',
    bankName: '',
    accountNumber: '',
    confirmAccountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    bankDocFile: null as File | null,
    upiDocFile: null as File | null
  });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [isEditingPayment, setIsEditingPayment] = useState(false);
  const [showBankProofWarning, setShowBankProofWarning] = useState(false);
  const [showUpiProofWarning, setShowUpiProofWarning] = useState(false);
  const [showDealerModal, setShowDealerModal] = useState(false);
  const [dealerInfo, setDealerInfo] = useState<any>(null);
  const [showAppPrefModal, setShowAppPrefModal] = useState(false);
  const [appLanguage, setAppLanguage] = useState<'en' | 'bn' | 'hi'>('en');
  const [lifetimeEarnings, setLifetimeEarnings] = useState<number>(0);
  const [lifetimeEarningsLoading, setLifetimeEarningsLoading] = useState(true);
  const { user, logout, completeProfile, refreshUserData, markKycStatusLocally } = useAuth();
  const { addNotification } = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const languageOptions = useMemo(
    () => [
      { code: 'en' as const, label: 'English' },
      { code: 'bn' as const, label: 'বাংলা (Bengali)' },
      { code: 'hi' as const, label: 'हिन्दी (Hindi)' }
    ],
    []
  );

  // Helper function to get level translation key
  const getLevelKey = (level: string | null): string => {
    if (!level) return 'locked';
    const levelMap: { [key: string]: string } = {
      'Master': 'master',
      'Diamond': 'diamond',
      'Platinum': 'platinum',
      'Gold': 'gold',
      'Silver': 'silver',
      'Bronze': 'bronze',
      'Brighten Up': 'brightenUp',
      'Locked': 'locked'
    };
    return levelMap[level] || 'locked';
  };

  // Track screen width for responsive design
  useEffect(() => {
    const updateScreenWidth = () => {
      setScreenWidth(window.innerWidth);
    };
    updateScreenWidth();
    window.addEventListener('resize', updateScreenWidth);
    return () => window.removeEventListener('resize', updateScreenWidth);
  }, []);

  // Load saved app language preference
  useEffect(() => {
    const savedLang = localStorage.getItem('bondhu_app_language') as 'en' | 'bn' | 'hi' | null;
    if (savedLang && ['en', 'bn', 'hi'].includes(savedLang)) {
      setAppLanguage(savedLang as 'en' | 'bn' | 'hi');
      i18n.changeLanguage(savedLang);
    } else {
      i18n.changeLanguage('en');
    }
  }, [i18n]);

  // Fetch lifetime earnings
  useEffect(() => {
    const fetchLifetimeEarnings = async () => {
      if (!user?.id) {
        setLifetimeEarningsLoading(false);
        return;
      }

      try {
        setLifetimeEarningsLoading(true);
        const history = await userAnalytics.getTransactionHistory(user.id);
        setLifetimeEarnings(history.lifetimeStats.totalEarned || 0);
      } catch (error) {
        console.error('Error fetching lifetime earnings:', error);
        setLifetimeEarnings(0);
      } finally {
        setLifetimeEarningsLoading(false);
      }
    };

    fetchLifetimeEarnings();
  }, [user?.id]);

  const handleSelectLanguage = (lang: 'en' | 'bn' | 'hi') => {
    setAppLanguage(lang);
    localStorage.setItem('bondhu_app_language', lang);
    i18n.changeLanguage(lang);
    const selected = languageOptions.find((l) => l.code === lang)?.label || lang;
    toast.success(`Language set to ${selected}`);
    setShowAppPrefModal(false);
  };

  // Fetch dealer information if dealerCode exists
  useEffect(() => {
    const fetchDealerInfo = async () => {
      // If we already have dealer info and user still has matching dealerCode, don't refetch
      if (dealerInfo && user?.dealerCode && dealerInfo.dealerCode === user.dealerCode) {
        return;
      }

      // Check if user has dealerCode or dealerId
      if (!user?.dealerCode && !user?.dealerId) {
        // Only clear if we don't have dealer info already cached
        if (!dealerInfo) {
          setDealerInfo(null);
        }
        return;
      }

      try {
        // Prefer customerDealerRelations (customer-readable) over dealers collection (blocked by rules)
        if (user?.id) {
          const relQuery = query(
            collection(db, 'customerDealerRelations'),
            where('customerId', '==', user.id),
            limit(1)
          );
          const relSnap = await getDocs(relQuery);
          if (!relSnap.empty) {
            const relDoc = relSnap.docs[0];
            const data = relDoc.data() as any;
            setDealerInfo({
              id: data.dealerId,
              name: data.dealerName || data.dealerBusinessName || data.dealerCode || user.dealerCode || 'Dealer',
              businessName: data.dealerBusinessName || undefined,
              dealerCode: data.dealerCode || user.dealerCode,
              phone: data.dealerPhone || undefined,
              email: undefined,
            });
            return;
          }
        }

        // Fallback: if relation not found, keep previous info if any
        if (!dealerInfo) {
          setDealerInfo(null);
        }
      } catch (error) {
        console.error('Error fetching dealer info:', error);
        if (!dealerInfo) {
          setDealerInfo(null);
        }
      }
    };

    fetchDealerInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.dealerId, user?.dealerCode]); // Removed dealerInfo from dependencies to prevent unnecessary refetches

  // Memoize expensive calculations
  // Live listen to user's successful scan transactions to compute actual scans
  useEffect(() => {
    if (!user?.id) {
      setRealScanCount(null);
      return;
    }
    try {
      const txRef = collection(db, 'transactions');
      const q = query(
        txRef,
        where('userId', '==', user.id),
        where('type', '==', 'cashback'),
        where('status', '==', 'completed')
      );
      const unsub = onSnapshot(q, (snap) => {
        setRealScanCount(snap.size);
      });
      return () => unsub();
    } catch {
      // Fallback: keep null so we use user.couponUsed if query fails
      setRealScanCount(null);
    }
  }, [user?.id]);

  const memberLevel = useMemo(() => {
    // Use points balance instead of scan count
    const points = Number(user?.pointsBalance) || 0;

    // New harder level thresholds (based on total points):
    // Locked:       0 – 99
    // Bronze:     100 – 499   (next: Gold,     target: 500)
    // Gold:       500 – 1499  (next: Platinum, target: 1500)
    // Platinum:  1500 – 2499  (next: Diamond,  target: 2500)
    // Diamond:   2500 – 4999  (next: Master,   target: 5000)
    // Master:    5000+        (max level)

    // 5000+ points - Master level (maximum achieved)
    if (points >= 5000) {
      return {
        level: 'Master',
        iconSrc: '/MASTER.png',
        color: '#ee4238',
        next: null,
        required: 5000,
        currentPoints: points,
        pointsToNext: 0
      };
    }

    // 2500–4999 points - Diamond level
    if (points >= 2500) {
      return {
        level: 'Diamond',
        iconSrc: '/DIAMOND.png',
        color: '#b9f2ff',
        next: 'Master',
        required: 5000,
        currentPoints: points,
        pointsToNext: 5000 - points
      };
    }

    // 1500–2499 points - Platinum level
    if (points >= 1500) {
      return {
        level: 'Platinum',
        iconSrc: '/PLATINAM.png',
        color: '#e5e4e2',
        next: 'Diamond',
        required: 2500,
        currentPoints: points,
        pointsToNext: 2500 - points
      };
    }

    // 500–1499 points - Gold level
    if (points >= 500) {
      return {
        level: 'Gold',
        iconSrc: '/GOLD.png',
        color: '#ffd700',
        next: 'Platinum',
        required: 1500,
        currentPoints: points,
        pointsToNext: 1500 - points
      };
    }

    // 100–499 points - Bronze level
    if (points >= 100) {
      return {
        level: 'Bronze',
        iconSrc: '/BRONZE.png',
        color: '#cd7f32',
        next: 'Gold',
        required: 500,
        currentPoints: points,
        pointsToNext: 500 - points
      };
    }

    // 0–99 points - Locked (no badge)
    return {
      level: 'Locked',
      iconSrc: null,
      color: '#666666',
      next: 'Bronze',
      required: 100,
      currentPoints: points,
      pointsToNext: 100 - points
    };
  }, [user?.pointsBalance]);

  const progressData = useMemo(() => {
    // Use points balance instead of scan count
    const points = Number(user?.pointsBalance) || 0;

    // Calculate progress correctly based on current level
    let progressPercent = 100;
    if (memberLevel.next) {
      // Calculate progress from current level to next level
      if (points < 100) {
        // Locked level: progress from 0 to 100 points
        progressPercent = (points / 100) * 100;
      } else if (points < 500) {
        // Bronze level: progress from 100 to 500 points
        progressPercent = ((points - 100) / 400) * 100;
      } else if (points < 1500) {
        // Gold level: progress from 500 to 1500 points
        progressPercent = ((points - 500) / 1000) * 100;
      } else if (points < 2500) {
        // Platinum level: progress from 1500 to 2500 points
        progressPercent = ((points - 1500) / 1000) * 100;
      } else if (points < 5000) {
        // Diamond level: progress from 2500 to 5000 points
        progressPercent = ((points - 2500) / 2500) * 100;
      }
    }

    return {
      points,
      progressPercent: Math.min(100, Math.max(0, progressPercent)),
      pointsToNext: memberLevel.pointsToNext
    };
  }, [user?.pointsBalance, memberLevel]);

  const memberSince = useMemo(() => {
    if (!user?.createdAt) return 0;
    return Math.floor((new Date().getTime() - user.createdAt.getTime()) / (1000 * 60 * 60 * 24));
  }, [user?.createdAt]);

  // Memoize handlers to prevent unnecessary re-renders
  const handlePhotoSelect = useCallback((file: File) => {
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      setShowPhotoModal(false);
    }
  }, []);

  const handlePhotoUpload = useCallback(async () => {
    if (!user || !photoPreview) return;

    setPhotoUploading(true);
    try {
      // Check if user is authenticated
      const { auth } = await import('../firebase');
      if (!auth.currentUser) {
        toast.error('You must be logged in to upload a profile picture');
        setPhotoUploading(false);
        return;
      }

      const response = await fetch(photoPreview);
      const blob = await response.blob();

      // Delete old photo if exists
      if (user.photoURL) {
        try {
          // Extract storage path from download URL
          // Firebase Storage URLs format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?alt=media&token={token}
          const url = new URL(user.photoURL);
          const pathMatch = url.pathname.match(/\/o\/(.+)\?/);
          if (pathMatch) {
            const encodedPath = pathMatch[1];
            const storagePath = decodeURIComponent(encodedPath);
            const oldPhotoRef = ref(storage, storagePath);
            await deleteObject(oldPhotoRef);
          }
        } catch (error) {
          // Ignore error if old photo doesn't exist or URL format is different
          console.log('Could not delete old photo:', error);
        }
      }

      const fileRef = ref(storage, `users/${user.id}/avatar-${Date.now()}`);
      await uploadBytes(fileRef, blob);
      const photoURL = await getDownloadURL(fileRef);

      await updateDoc(doc(db, 'users', user.id), {
        photoURL: photoURL
      });

      toast.success('Profile picture updated successfully!');
      setPhotoPreview(null);
      // Refresh user data without page reload
      await refreshUserData();
    } catch (error: any) {
      console.error('Photo upload error:', error);

      // Handle specific Firebase Storage errors
      let errorMessage = 'Failed to update profile picture';

      if (error?.code) {
        switch (error.code) {
          case 'storage/unauthorized':
            errorMessage = 'Upload permission denied. Please make sure you are logged in and try again.';
            break;
          case 'storage/canceled':
            errorMessage = 'Upload was canceled. Please try again.';
            break;
          case 'storage/unknown':
            errorMessage = 'An unknown error occurred. Please try again.';
            break;
          case 'storage/invalid-format':
            errorMessage = 'Invalid file format. Please select a valid image file.';
            break;
          case 'storage/retry-limit-exceeded':
            errorMessage = 'Upload failed after multiple attempts. Please check your internet connection and try again.';
            break;
          case 'storage/quota-exceeded':
            errorMessage = 'Storage quota exceeded. Please contact support.';
            break;
          case 'storage/unauthenticated':
            errorMessage = 'You must be logged in to upload a profile picture.';
            break;
          case 'permission-denied':
            errorMessage = 'Permission denied. Please make sure you are logged in with the correct account.';
            break;
          default:
            errorMessage = error?.message || 'Failed to update profile picture. Please try again.';
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setPhotoUploading(false);
    }
  }, [user, photoPreview, refreshUserData]);

  const handleRemovePhoto = useCallback(async () => {
    if (!user || !user.photoURL) return;

    const confirmed = window.confirm('Are you sure you want to remove your profile picture?');
    if (!confirmed) return;

    setPhotoUploading(true);
    try {
      // Extract storage path from download URL
      const url = new URL(user.photoURL);
      const pathMatch = url.pathname.match(/\/o\/(.+)\?/);
      if (pathMatch) {
        const encodedPath = pathMatch[1];
        const storagePath = decodeURIComponent(encodedPath);
        const photoRef = ref(storage, storagePath);
        await deleteObject(photoRef);
      }

      await updateDoc(doc(db, 'users', user.id), {
        photoURL: ''
      });

      toast.success('Profile picture removed successfully!');
      setShowPhotoModal(false);
      await refreshUserData();
    } catch (error: any) {
      console.error('Remove photo error:', error);
      const errorMessage = error?.message || 'Failed to remove profile picture';
      toast.error(errorMessage);
    } finally {
      setPhotoUploading(false);
    }
  }, [user, refreshUserData]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      toast.error('Failed to logout');
    }
  }, [logout]);

  // Edit Profile Functions
  const handleEditProfile = useCallback(() => {
    if (user) {
      setEditFormData({
        name: user.name || '',
        phone: user.phone || '',
        upiId: user.upiId || '',
        state: user.state || '',
        district: user.district || '',
        city: user.city || '',
        pinCode: user.pinCode || '',
        photoFile: null,
        address: user.address || '',
        age: user.age,
        accountType: 'Painter'
      });
      const parts = (user.name || '').trim().split(' ');
      setEditFirstName(parts[0] || '');
      setEditLastName(parts.slice(1).join(' ') || '');
      setShowEditProfileModal(true);
    }
  }, [user]);

  // KYC state
  const [kycRealName, setKycRealName] = useState('');
  const [kycAadhaarNumber, setKycAadhaarNumber] = useState('');
  const [kycPanNumber, setKycPanNumber] = useState('');
  const [kycAadhaarPreview, setKycAadhaarPreview] = useState<string | null>(null);
  const [kycPanPreview, setKycPanPreview] = useState<string | null>(null);
  const [kycSaving, setKycSaving] = useState(false);

  const handleOpenKyc = useCallback(() => {
    if (user) {
      setKycRealName(user.kyc?.realName || user.name || '');
      setKycAadhaarNumber(user.kyc?.aadhaarNumber || '');
      setKycPanNumber(user.kyc?.panNumber || '');
      setKycAadhaarPreview(user.kyc?.aadhaarPhotoURL || null);
      setKycPanPreview(user.kyc?.panPhotoURL || null);
      setShowKycModal(true);
    }
  }, [user]);

  const onSelectKycImage = async (e: React.ChangeEvent<HTMLInputElement>, type: 'aadhaar' | 'pan') => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result as string;
      if (type === 'aadhaar') setKycAadhaarPreview(url);
      if (type === 'pan') setKycPanPreview(url);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitKyc = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Check if user is blocked
    if (user?.kyc?.blockPermanent) {
      toast.error('Your KYC submission is permanently blocked. Please contact support.');
      return;
    }
    if (user?.kyc?.blockUntil) {
      const blockDate = user.kyc.blockUntil;
      if (blockDate > new Date()) {
        const daysLeft = Math.ceil((blockDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        toast.error(`You are temporarily blocked. Try again after ${daysLeft} day${daysLeft > 1 ? 's' : ''}.`);
        return;
      }
    }

    const hasAadhaarPhoto = !!kycAadhaarPreview || !!user?.kyc?.aadhaarPhotoURL;
    const hasPanPhoto = !!kycPanPreview || !!user?.kyc?.panPhotoURL;
    if (!kycRealName.trim() || kycAadhaarNumber.replace(/\D/g, '').length !== 12 || kycPanNumber.trim().length < 8 || !hasAadhaarPhoto || !hasPanPhoto) {
      toast.error('Please fill valid KYC details');
      return;
    }
    setKycSaving(true);
    try {
      // Upload images if changed (data URLs)
      let aadhaarURL = user.kyc?.aadhaarPhotoURL || '';
      let panURL = user.kyc?.panPhotoURL || '';

      const uploadDataUrl = async (dataUrl: string, path: string) => {
        try {
          const res = await fetch(dataUrl);
          const blob = await res.blob();

          // Check blob size (should already be validated, but double-check)
          const maxSize = 5 * 1024 * 1024; // 5MB
          if (blob.size > maxSize) {
            throw new Error(`File size (${(blob.size / (1024 * 1024)).toFixed(2)}MB) exceeds 5MB limit`);
          }

          const fileRef = ref(storage, path);
          await uploadBytes(fileRef, blob);
          return await getDownloadURL(fileRef);
        } catch (error: any) {
          if (error.code === 'storage/quota-exceeded') {
            throw new Error('Storage quota exceeded. Please contact support.');
          } else if (error.code === 'storage/unauthorized') {
            throw new Error('Upload permission denied. Please contact support.');
          } else if (error.code === 'storage/canceled') {
            throw new Error('Upload was canceled. Please try again.');
          } else if (error.message?.includes('exceeds')) {
            throw error; // Re-throw size errors
          } else {
            throw new Error(`Upload failed: ${error.message || 'Unknown error'}`);
          }
        }
      };

      if (kycAadhaarPreview && !kycAadhaarPreview.startsWith('http')) {
        aadhaarURL = await uploadDataUrl(kycAadhaarPreview, `users/${user.id}/kyc/aadhaar-${Date.now()}.jpg`);
      }
      if (kycPanPreview && !kycPanPreview.startsWith('http')) {
        panURL = await uploadDataUrl(kycPanPreview, `users/${user.id}/kyc/pan-${Date.now()}.jpg`);
      }

      // Use field-level updates to preserve submitCount and increment it
      await updateDoc(doc(db, 'users', user.id), {
        'kyc.realName': kycRealName.trim(),
        'kyc.aadhaarNumber': kycAadhaarNumber.replace(/\D/g, ''),
        'kyc.aadhaarPhotoURL': aadhaarURL,
        'kyc.panNumber': kycPanNumber.toUpperCase().trim(),
        'kyc.panPhotoURL': panURL,
        'kyc.status': 'submitted',
        'kyc.submittedAt': new Date(),
        'kyc.submitCount': increment(1),
        'kyc.rejectReason': '',
        'kyc.needsBankResubmit': true, // Force bank resubmit flow
      });
      toast.success('KYC submitted for review');

      // Send KYC submission notification
      addNotification({
        title: 'KYC Submitted',
        message: 'Your KYC documents have been submitted for admin review',
        type: 'kyc',
        time: 'Just now'
      });

      setShowKycModal(false);
      await refreshUserData();
    } catch (err: any) {
      console.error('KYC submission error:', err);
      let errorMessage = 'Failed to submit KYC';

      if (err.message?.includes('quota')) {
        errorMessage = 'Storage quota exceeded. Please contact support.';
      } else if (err.message?.includes('permission') || err.message?.includes('unauthorized')) {
        errorMessage = 'Upload permission denied. Please contact support.';
      } else if (err.message?.includes('exceeds') || err.message?.includes('size')) {
        errorMessage = err.message || 'File size too large. Please compress images before uploading.';
      } else if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please contact support or try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      toast.error(errorMessage);
    } finally {
      setKycSaving(false);
    }
  }, [user, kycRealName, kycAadhaarNumber, kycPanNumber, kycAadhaarPreview, kycPanPreview, refreshUserData]);

  const handleEditInputChange = useCallback((field: keyof ProfileData, value: string) => {
    setEditFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      // Reset dependent fields when state changes
      if (field === 'state') {
        newData.district = '';
        newData.city = '';
        newData.pinCode = '';
      }

      // Reset dependent fields when district changes
      if (field === 'district') {
        newData.city = '';
        newData.pinCode = '';
      }

      return newData;
    });
  }, []);

  const handleEditSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setEditLoading(true);

    try {
      await completeProfile({
        ...editFormData,
        name: `${editFirstName} ${editLastName}`.trim(),
        phone: user?.phone || editFormData.phone || '',
        accountType: 'Painter',
        age: editFormData.age ? Number(editFormData.age) : undefined,
      });
      setShowEditProfileModal(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Profile update error:', error);
    } finally {
      setEditLoading(false);
    }
  }, [editFormData, editFirstName, editLastName, completeProfile, user?.phone]);

  // Payment Info Functions
  const handlePaymentInfo = useCallback(async () => {
    if (user) {
      // Refresh user data from Firestore to get latest bank details
      try {
        const userDoc = await getDoc(doc(db, 'users', user.id));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('🔄 Fresh User Data from Firestore:', userData);

          // Determine which payment method is currently set
          const hasUpi = userData.upiId && userData.upiId.trim() !== '';
          const hasBank = userData.bankName && userData.bankName.trim() !== '';

          console.log('🔍 Payment Info Debug:', {
            hasUpi,
            hasBank,
            upiId: userData.upiId,
            bankName: userData.bankName,
            accountNumber: userData.accountNumber,
            ifscCode: userData.ifscCode,
            accountHolderName: userData.accountHolderName
          });

          // Prioritize bank details (bank is required, UPI is optional)
          const paymentMethod = hasBank ? 'bank' : hasUpi ? 'upi' : 'bank';

          console.log('🎯 Selected Payment Method:', paymentMethod);

          setPaymentFormData({
            paymentMethod: paymentMethod,
            upiId: userData.upiId || '',
            bankName: userData.bankName || '',
            accountNumber: userData.accountNumber || '',
            confirmAccountNumber: '',
            ifscCode: userData.ifscCode || '',
            accountHolderName: userData.accountHolderName || '',
            bankDocFile: null,
            upiDocFile: null
          });
          setIsEditingPayment(false);
          setShowPaymentModal(true);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Fallback to current user data
        const hasUpi = user.upiId && user.upiId.trim() !== '';
        const hasBank = user.bankName && user.bankName.trim() !== '';

        setPaymentFormData({
          paymentMethod: hasBank ? 'bank' : hasUpi ? 'upi' : 'bank',
          upiId: user.upiId || '',
          bankName: user.bankName || '',
          accountNumber: user.accountNumber || '',
          confirmAccountNumber: '',
          ifscCode: user.ifscCode || '',
          accountHolderName: user.accountHolderName || '',
          bankDocFile: null,
          upiDocFile: null
        });
        setIsEditingPayment(false);
        setShowPaymentModal(true);
      }
    }
  }, [user]);

  // Auto-open payment modal if openPayment query parameter is present
  useEffect(() => {
    const openPayment = searchParams.get('openPayment');
    if (openPayment === 'true' && user && !showPaymentModal) {
      // Remove the query parameter from URL
      searchParams.delete('openPayment');
      setSearchParams(searchParams, { replace: true });
      // Open payment modal
      handlePaymentInfo();
    }
  }, [searchParams, setSearchParams, user, showPaymentModal, handlePaymentInfo]);

  const handlePaymentInputChange = useCallback((field: string, value: any) => {
    setPaymentFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handlePaymentSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setPaymentLoading(true);

    try {
      // Check 60-day cooldown period
      if (user?.paymentChangeRequest?.submittedAt) {
        const lastSubmit = user.paymentChangeRequest.submittedAt;
        const daysSinceLastSubmit = Math.floor((Date.now() - lastSubmit.getTime()) / (1000 * 60 * 60 * 24));
        if (daysSinceLastSubmit < 60) {
          const daysLeft = 60 - daysSinceLastSubmit;
          toast.error(`You can submit another payment change request after ${daysLeft} days.`);
          setPaymentLoading(false);
          return;
        }
      }

      // Validate bank account details if bank method is selected
      if (paymentFormData.paymentMethod === 'bank') {
        // Check if account numbers match
        if (paymentFormData.accountNumber !== paymentFormData.confirmAccountNumber) {
          toast.error('Account numbers do not match. Please check and try again.');
          setPaymentLoading(false);
          return;
        }

        // Check if all required fields are filled
        if (!paymentFormData.accountHolderName || !paymentFormData.bankName || !paymentFormData.accountNumber || !paymentFormData.ifscCode) {
          toast.error('Please fill in all required bank details.');
          setPaymentLoading(false);
          return;
        }

        // Require Bank proof document
        if (!paymentFormData.bankDocFile) {
          setShowBankProofWarning(true);
          toast.error('⚠️ Please upload bank proof document (Passbook/Cheque/Statement).', {
            duration: 6000,
            style: {
              background: '#fef3c7',
              color: '#92400e',
              border: '2px solid #fbbf24',
              fontSize: '15px',
              fontWeight: '600',
              zIndex: 999999,
            }
          });
          setPaymentLoading(false);
          return;
        }

        // Validate file type for bank doc
        const bankFile = paymentFormData.bankDocFile as File;
        const bankExt = bankFile.name.split('.').pop()?.toLowerCase();
        if (!['jpg', 'jpeg', 'png'].includes(bankExt || '')) {
          toast.error('❌ Bank proof must be JPG or PNG format only.', {
            duration: 5000,
            style: {
              background: '#fee2e2',
              color: '#991b1b',
              border: '2px solid #fca5a5',
              fontSize: '15px',
              fontWeight: '600',
              zIndex: 999999,
            }
          });
          setPaymentLoading(false);
          return;
        }
      }

      // Validate UPI ID if UPI method is selected
      if (paymentFormData.paymentMethod === 'upi') {
        if (!paymentFormData.upiId || paymentFormData.upiId.trim() === '') {
          toast.error(t('profile.payment.validUpi'));
          setPaymentLoading(false);
          return;
        }

        // Require UPI proof document
        if (!paymentFormData.upiDocFile) {
          setShowUpiProofWarning(true);
          toast.error('⚠️ Please upload UPI proof document (QR/Screenshot).', {
            duration: 6000,
            style: {
              background: '#fef3c7',
              color: '#92400e',
              border: '2px solid #fbbf24',
              fontSize: '15px',
              fontWeight: '600',
              zIndex: 999999,
            }
          });
          setPaymentLoading(false);
          return;
        }

        // Validate file type for UPI doc
        const upiFile = paymentFormData.upiDocFile as File;
        const upiExt = upiFile.name.split('.').pop()?.toLowerCase();
        if (!['jpg', 'jpeg', 'png'].includes(upiExt || '')) {
          toast.error('❌ UPI proof must be JPG or PNG format only.', {
            duration: 5000,
            style: {
              background: '#fee2e2',
              color: '#991b1b',
              border: '2px solid #fca5a5',
              fontSize: '15px',
              fontWeight: '600',
              zIndex: 999999,
            }
          });
          setPaymentLoading(false);
          return;
        }
      }

      // Prepare proposed change object instead of direct write
      const proposed: any = {};
      let type: 'bank' | 'upi' | 'both' = 'bank';
      if (paymentFormData.paymentMethod === 'upi') {
        proposed.upiId = paymentFormData.upiId;
        type = 'upi';
      } else if (paymentFormData.paymentMethod === 'bank') {
        proposed.bankName = paymentFormData.bankName;
        proposed.accountNumber = paymentFormData.accountNumber;
        proposed.ifscCode = paymentFormData.ifscCode;
        proposed.accountHolderName = paymentFormData.accountHolderName;
        type = 'bank';
      }
      // Helper function to compress image
      const compressImage = async (file: File): Promise<Blob> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              let width = img.width;
              let height = img.height;

              // Resize if larger than 1920px
              const maxSize = 1920;
              if (width > maxSize || height > maxSize) {
                if (width > height) {
                  height = (height / width) * maxSize;
                  width = maxSize;
                } else {
                  width = (width / height) * maxSize;
                  height = maxSize;
                }
              }

              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              ctx?.drawImage(img, 0, 0, width, height);

              canvas.toBlob(
                (blob) => {
                  if (blob) resolve(blob);
                  else reject(new Error('Compression failed'));
                },
                'image/jpeg',
                0.85 // 85% quality
              );
            };
            img.onerror = reject;
            img.src = e.target?.result as string;
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      };

      // Upload required documents with compression
      try {
        // Check authentication before upload
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        if (!auth.currentUser) {
          console.error('❌ User not authenticated');
          toast.error('Please log in again to upload documents.', {
            duration: 5000,
            style: {
              background: '#fee2e2',
              color: '#991b1b',
              border: '2px solid #fca5a5',
              fontSize: '15px',
              fontWeight: '600',
              zIndex: 999999,
            }
          });
          setPaymentLoading(false);
          return;
        }

        console.log('✅ User authenticated:', auth.currentUser.uid);
        console.log('✅ User ID from context:', user!.id);

        if (paymentFormData.bankDocFile) {
          const file = paymentFormData.bankDocFile as File;
          console.log('📤 Uploading bank document:', file.name, file.size, 'bytes');

          let compressedBlob: Blob;
          try {
            compressedBlob = await compressImage(file);
            console.log('✅ Image compressed:', compressedBlob.size, 'bytes');
          } catch (compressError) {
            console.error('❌ Image compression failed:', compressError);
            toast.error('Failed to compress bank document image. Please try a different image.', {
              duration: 5000,
              style: {
                background: '#fee2e2',
                color: '#991b1b',
                border: '2px solid #fca5a5',
                fontSize: '15px',
                fontWeight: '600',
                zIndex: 999999,
              }
            });
            setPaymentLoading(false);
            return;
          }

          const path = `users/${user!.id}/bank/bank-proof-${Date.now()}.jpg`;
          console.log('📁 Upload path:', path);
          console.log('📁 Expected rule match: users/{userId}/{fileName} where userId =', user!.id, 'and fileName = bank/bank-proof-...');

          try {
            const snap = await uploadBytes(ref(storage, path), compressedBlob);
            console.log('✅ Upload successful:', snap.metadata.fullPath);
            const url = await getDownloadURL(snap.ref);
            console.log('✅ Download URL:', url);
            proposed.bankDocUrl = url;
          } catch (uploadError: any) {
            console.error('❌ Firebase upload failed:', uploadError);
            const errorMessage = uploadError?.code === 'storage/unauthorized'
              ? 'Storage permission denied. Please contact support.'
              : uploadError?.code === 'storage/quota-exceeded'
                ? 'Storage quota exceeded. Please contact support.'
                : uploadError?.message || 'Failed to upload bank document to server.';

            toast.error(`❌ ${errorMessage}`, {
              duration: 6000,
              style: {
                background: '#fee2e2',
                color: '#991b1b',
                border: '2px solid #fca5a5',
                fontSize: '15px',
                fontWeight: '600',
                zIndex: 999999,
              }
            });
            setPaymentLoading(false);
            return;
          }
        }

        if (paymentFormData.upiDocFile) {
          const file = paymentFormData.upiDocFile as File;
          console.log('📤 Uploading UPI document:', file.name, file.size, 'bytes');

          let compressedBlob: Blob;
          try {
            compressedBlob = await compressImage(file);
            console.log('✅ Image compressed:', compressedBlob.size, 'bytes');
          } catch (compressError) {
            console.error('❌ Image compression failed:', compressError);
            toast.error('Failed to compress UPI document image. Please try a different image.', {
              duration: 5000,
              style: {
                background: '#fee2e2',
                color: '#991b1b',
                border: '2px solid #fca5a5',
                fontSize: '15px',
                fontWeight: '600',
                zIndex: 999999,
              }
            });
            setPaymentLoading(false);
            return;
          }

          const path = `users/${user!.id}/bank/upi-proof-${Date.now()}.jpg`;
          console.log('📁 Upload path:', path);

          try {
            const snap = await uploadBytes(ref(storage, path), compressedBlob);
            console.log('✅ Upload successful:', snap.metadata.fullPath);
            const url = await getDownloadURL(snap.ref);
            console.log('✅ Download URL:', url);
            proposed.upiDocUrl = url;
          } catch (uploadError: any) {
            console.error('❌ Firebase upload failed:', uploadError);
            const errorMessage = uploadError?.code === 'storage/unauthorized'
              ? 'Storage permission denied. Please contact support.'
              : uploadError?.code === 'storage/quota-exceeded'
                ? 'Storage quota exceeded. Please contact support.'
                : uploadError?.message || 'Failed to upload UPI document to server.';

            toast.error(`❌ ${errorMessage}`, {
              duration: 6000,
              style: {
                background: '#fee2e2',
                color: '#991b1b',
                border: '2px solid #fca5a5',
                fontSize: '15px',
                fontWeight: '600',
                zIndex: 999999,
              }
            });
            setPaymentLoading(false);
            return;
          }
        }
      } catch (e: any) {
        console.error('❌ Unexpected error during document upload:', e);
        const errorMessage = e?.message || 'An unexpected error occurred. Please try again.';
        toast.error(`❌ ${errorMessage}`, {
          duration: 6000,
          style: {
            background: '#fee2e2',
            color: '#991b1b',
            border: '2px solid #fca5a5',
            fontSize: '15px',
            fontWeight: '600',
            zIndex: 999999,
          }
        });
        setPaymentLoading(false);
        return;
      }

      // Save as pending paymentChangeRequest for admin review
      const paymentChangeRequestData = {
        status: 'pending',
        type,
        ...proposed,
        submittedAt: serverTimestamp()
      };

      console.log('🔵 [handlePaymentSubmit] Starting Firestore update...');
      console.log('🔵 [handlePaymentSubmit] User ID:', user!.id);
      console.log('🔵 [handlePaymentSubmit] Document path: users/' + user!.id);
      console.log('🔵 [handlePaymentSubmit] Payment change request data to upload:', {
        status: 'pending',
        type: type,
        ...proposed,
        submittedAt: 'serverTimestamp()'
      });
      console.log('🔵 [handlePaymentSubmit] Proposed fields:', proposed);
      console.log('🔵 [handlePaymentSubmit] Payment method:', paymentFormData.paymentMethod);

      try {
        await updateDoc(doc(db, 'users', user!.id), {
          paymentChangeRequest: paymentChangeRequestData
        });
        console.log('✅ [handlePaymentSubmit] Firestore update successful!');
      } catch (updateError: any) {
        console.error('❌ [handlePaymentSubmit] Firestore update error:', updateError);
        console.error('❌ [handlePaymentSubmit] Error code:', updateError.code);
        console.error('❌ [handlePaymentSubmit] Error message:', updateError.message);
        console.error('❌ [handlePaymentSubmit] Full error object:', JSON.stringify(updateError, null, 2));

        // Log which specific field might be causing the issue
        if (updateError.code === 'permission-denied') {
          console.error('❌ [handlePaymentSubmit] PERMISSION DENIED - Check Firestore rules for users collection');
          console.error('❌ [handlePaymentSubmit] Attempted to update document: users/' + user!.id);
          console.error('❌ [handlePaymentSubmit] User authenticated:', !!user);
          console.error('❌ [handlePaymentSubmit] User UID:', user!.id);
          console.error('❌ [handlePaymentSubmit] Payment change request data:', paymentChangeRequestData);
          console.error('❌ [handlePaymentSubmit] Proposed fields that failed:', proposed);
        }

        throw updateError;
      }

      setShowPaymentModal(false);
      toast.success('Payment change submitted for admin review', {
        style: {
          zIndex: 999999,
          fontSize: '15px',
          fontWeight: '600',
        },
      });

      // Send payment change request notification
      addNotification({
        title: 'Payment Change Request',
        message: 'Your bank/UPI change request has been submitted for admin review',
        type: 'payment',
        time: 'Just now'
      });

      // Refresh user data without page reload
      await refreshUserData();
    } catch (error) {
      console.error('Payment update error:', error);
      toast.error('Failed to update payment information');
    } finally {
      setPaymentLoading(false);
    }
  }, [paymentFormData, user, refreshUserData]);

  const handleEditPayment = useCallback(() => {
    // Cooldown check is now handled by button disabled state and banner
    // No need for toast since banner is always visible
    setShowBankProofWarning(false);
    setShowUpiProofWarning(false);
    setIsEditingPayment(true);
  }, []);

  const handleAddNewPayment = useCallback(() => {
    setPaymentFormData({
      paymentMethod: 'bank',
      upiId: '',
      bankName: '',
      accountNumber: '',
      confirmAccountNumber: '',
      ifscCode: '',
      accountHolderName: '',
      bankDocFile: null,
      upiDocFile: null
    });
    setIsEditingPayment(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    // Reset to current user data
    if (user) {
      const hasUpi = user.upiId && user.upiId.trim() !== '';
      const hasBank = user.bankName && user.bankName.trim() !== '';

      setPaymentFormData({
        paymentMethod: hasBank ? 'bank' : hasUpi ? 'upi' : 'bank',
        upiId: user.upiId || '',
        bankName: user.bankName || '',
        accountNumber: user.accountNumber || '',
        confirmAccountNumber: '',
        ifscCode: user.ifscCode || '',
        accountHolderName: user.accountHolderName || '',
        bankDocFile: null,
        upiDocFile: null
      });
    }
    setShowBankProofWarning(false);
    setShowUpiProofWarning(false);
    setIsEditingPayment(false);
  }, [user]);

  // Add CSS animations only once
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes glow {
        0% { 
          background: radial-gradient(circle, rgba(38, 103, 255, 0.9) 0%, rgba(38, 103, 255, 0.6) 20%, rgba(38, 103, 255, 0.2) 35%, transparent 45%);
          transform: translate(-50%, -50%) scale(1);
        }
        100% { 
          background: radial-gradient(circle, rgba(38, 103, 255, 1.0) 0%, rgba(38, 103, 255, 0.8) 20%, rgba(38, 103, 255, 0.3) 35%, transparent 45%);
          transform: translate(-50%, -50%) scale(1.05);
        }
      }
      
      @keyframes hexGlow {
        0% {
          box-shadow: 0 0 20px rgba(255, 20, 147, 0.6), 0 0 40px rgba(255, 20, 147, 0.4);
          transform: translateX(-50%) scale(1);
        }
        100% {
          box-shadow: 0 0 30px rgba(255, 20, 147, 0.8), 0 0 60px rgba(255, 20, 147, 0.6);
          transform: translateX(-50%) scale(1.02);
        }
      }
      
      @keyframes fireGlow {
        0% {
          box-shadow: 0 0 20px rgba(255, 107, 53, 0.8), 0 0 40px rgba(255, 107, 53, 0.6), 0 0 60px rgba(255, 107, 53, 0.4);
          transform: translateX(-50%) scale(1);
        }
        50% {
          box-shadow: 0 0 30px rgba(255, 107, 53, 1.0), 0 0 50px rgba(255, 107, 53, 0.8), 0 0 70px rgba(255, 107, 53, 0.6);
          transform: translateX(-50%) scale(1.05);
        }
        100% {
          box-shadow: 0 0 20px rgba(255, 107, 53, 0.8), 0 0 40px rgba(255, 107, 53, 0.6), 0 0 60px rgba(255, 107, 53, 0.4);
          transform: translateX(-50%) scale(1);
        }
      }
      
      @keyframes fireMove {
        0%, 100% {
          box-shadow: 0 0 15px #ff4500, inset 0 0 15px #ff4500;
        }
        50% {
          box-shadow: 0 0 24px #ff4500, inset 0 0 24px #ff4500;
        }
      }
      
      @keyframes fireFlicker {
        0%, 100% {
          opacity: 1;
          transform: scale(1);
        }
        25% {
          opacity: 0.8;
          transform: scale(1.1);
        }
        50% {
          opacity: 1;
          transform: scale(0.9);
        }
        75% {
          opacity: 0.9;
          transform: scale(1.05);
        }
      }
      
      @keyframes fireRise {
        0% {
          transform: translateY(0px) scale(1);
          opacity: 1;
        }
        50% {
          transform: translateY(-5px) scale(1.1);
          opacity: 0.8;
        }
        100% {
          transform: translateY(-10px) scale(0.8);
          opacity: 0;
        }
      }
      
      @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-10px) rotate(5deg); }
      }
      
      @keyframes shimmer {
        0% { opacity: 0.5; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.05); }
        100% { opacity: 0.5; transform: scale(1); }
      }
      
      @keyframes pulse {
        0%, 100% { transform: scale(1); opacity: 0.7; }
        50% { transform: scale(1.1); opacity: 1; }
      }
      
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  if (!user) {
    return <LoadingSpinner />;
  }



  const { points, progressPercent, pointsToNext } = progressData;



  // Styles
  const containerStyle = {
    minHeight: '100vh',
    width: '100vw',
    background: '#eef3f9',
    paddingTop: '0px',
    paddingBottom: 'calc(env(safe-area-inset-bottom) + 80px)',
    margin: 0,
    overflowX: 'hidden' as const
    // fontFamily will inherit from body
  };

  const heroCardStyle = {
    width: '100%',
    marginLeft: 0,
    marginRight: 0,
    background: '#ffffff',
    borderRadius: '0 0 20px 20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
    position: 'relative' as const,
    overflow: 'hidden',
    marginTop: '0px',
    marginBottom: '24px',
    border: '1px solid rgba(0, 0, 0, 0.04)',
    paddingLeft: '0',
    paddingRight: '0'
  };

  const bannerStyle = {
    minHeight: '80px',
    height: 'clamp(80px, 18vw, 120px)',
    // Match MainLoginPage hero theme (brand red gradient)
    background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
    position: 'relative' as const,
    overflow: 'hidden'
  };

  const avatarStyle = {
    width: '110px',
    height: '110px',
    borderRadius: '50%',
    border: '5px solid #ffffff',
    position: 'absolute' as const,
    top: 'calc(clamp(80px, 18vw, 120px) - 55px)', // banner height - (avatar height / 2) - now responsive
    left: '50%',
    transform: 'translateX(-50%)',
    objectFit: 'cover' as const,
    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15), 0 6px 20px rgba(0, 0, 0, 0.08), 0 0 0 2px rgba(255, 255, 255, 0.8)',
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  };

  const editBadgeStyle = {
    position: 'absolute' as const,
    bottom: '5px',
    right: '5px',
    width: '36px',
    height: '36px',
    background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(255, 107, 53, 0.4), 0 2px 8px rgba(0, 0, 0, 0.1)',
    color: 'white',
    border: '3px solid #ffffff',
    transition: 'all 0.3s ease',
    animation: 'pulse 2s ease-in-out infinite'
  };

  const contentStyle = {
    padding: 'calc(55px + 20px) 16px 28px',
    textAlign: 'center' as const,
    width: '100%'
  };

  const nameStyle = {
    fontSize: 'clamp(20px, 5vw, 24px)',
    fontWeight: '700',
    marginBottom: '8px',
    marginTop: '8px',
    color: '#1a1a1a',
    letterSpacing: '-0.02em',
    lineHeight: '1.2'
  };

  const emailStyle = {
    fontSize: 'clamp(12px, 3vw, 14px)',
    color: '#6b7280',
    marginBottom: '18px',
    fontWeight: '400',
    letterSpacing: '0.01em'
  };

  const actionsStyle = {
    display: 'flex',
    justifyContent: 'center',
    gap: '14px',
    marginBottom: '20px',
    padding: '0 8px'
  };

  const buttonStyle = {
    padding: '10px 20px',
    borderRadius: '12px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    letterSpacing: '0.01em'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
    color: '#ffffff',
    boxShadow: '0 4px 12px rgba(230, 66, 56, 0.35), 0 2px 8px rgba(0, 0, 0, 0.1)'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
    color: '#ffffff',
    boxShadow: '0 4px 12px rgba(31, 41, 55, 0.3), 0 2px 8px rgba(0, 0, 0, 0.1)'
  };

  const levelBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    padding: '0px',
    borderRadius: '50%',
    width: '60px',
    height: '60px',
    marginBottom: '16px',
    position: 'relative' as const
  };

  const infoSectionStyle = {
    textAlign: 'left' as const,
    background: '#f8f9fa',
    padding: '16px',
    borderRadius: '12px',
    marginTop: '20px'
  };

  const infoTitleStyle = {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '6px',
    color: '#1a1a1a'
  };

  const infoDescStyle = {
    fontSize: '14px',
    color: '#4b5563',
    margin: 0
  };

  const statsRowStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    marginBottom: '24px'
  };

  const statCardStyle = {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '16px',
    textAlign: 'center' as const,
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid #f0f0f0'
  };

  const statValueStyle = {
    fontSize: '20px',
    fontWeight: '700',
    color: '#ee4238',
    marginBottom: '4px'
  };

  const statLabelStyle = {
    fontSize: '12px',
    color: '#666',
    fontWeight: '500'
  };

  const progressCardStyle = {
    background: '#ffffff',
    borderRadius: '20px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid #f0f0f0'
  };

  const progressHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  };

  const progressTitleStyle = {
    fontSize: '18px',
    fontWeight: '700',
    color: '#ee4238'
  };

  const progressBarStyle = {
    width: '100%',
    height: '10px',
    background: 'rgba(230, 66, 56, 0.15)',
    borderRadius: '6px',
    overflow: 'hidden',
    marginBottom: '12px'
  };

  const progressFillStyle = {
    height: '100%',
    background: 'linear-gradient(90deg, #ee4238 0%, #d63a32 100%)',
    borderRadius: '6px',
    width: `${Math.min(100, progressPercent)}%`,
    transition: 'width 0.8s ease'
  };

  const progressTextStyle = {
    fontSize: '14px',
    color: '#666',
    textAlign: 'center' as const
  };

  const settingsCardStyle = {
    background: '#ffffff',
    borderRadius: '20px',
    padding: '8px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid #f0f0f0'
  };

  const settingsItemStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    gap: '16px'
  };

  const settingsIconStyle = {
    width: '24px',
    height: '24px',
    color: '#ee4238'
  };

  const settingsTextStyle = {
    flex: 1,
    fontSize: '16px',
    fontWeight: '500',
    color: '#1a1a1a'
  };

  const logoutItemStyle = {
    ...settingsItemStyle,
    color: '#dc3545'
  };

  const logoutIconStyle = {
    ...settingsIconStyle,
    color: '#dc3545'
  };

  const modalOverlayStyle = {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000
  };

  const modalContentStyle = {
    background: 'white',
    borderRadius: '20px',
    padding: '24px',
    maxWidth: '320px',
    width: '90%',
    textAlign: 'center' as const
  };

  const modalButtonStyle = {
    width: '100%',
    padding: '12px',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginBottom: '12px',
    border: 'none'
  };

  const modalPrimaryButtonStyle = {
    ...modalButtonStyle,
    background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
    color: 'white'
  };

  const modalSecondaryButtonStyle = {
    ...modalButtonStyle,
    background: '#f8f9fa',
    color: '#666',
    border: '1px solid #e9ecef'
  };

  return (
    <div style={containerStyle}>
      {/* Hero Card Section */}
      <div style={heroCardStyle}>
        {/* Banner Background */}
        <div style={bannerStyle}>
          {/* Animated Background Elements */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '20px 20px',
            animation: 'float 20s ease-in-out infinite'
          }}></div>

          {/* Floating Orbs */}
          <div style={{
            position: 'absolute',
            top: '20px',
            right: '30px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            animation: 'float 6s ease-in-out infinite'
          }}></div>

          <div style={{
            position: 'absolute',
            bottom: '30px',
            left: '40px',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.15)',
            animation: 'float 8s ease-in-out infinite reverse'
          }}></div>

          {/* Gradient Overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%, rgba(0, 0, 0, 0.05) 100%)',
            pointerEvents: 'none'
          }}></div>
        </div>

        {/* Avatar - Overlapping banner and content */}
        <div style={avatarStyle}
          onClick={() => setShowPhotoModal(true)}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateX(-50%) scale(1.05)';
            e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 0, 0, 0.25), 0 12px 32px rgba(0, 0, 0, 0.15), 0 0 0 3px rgba(255, 255, 255, 0.9)';
            const overlay = e.currentTarget.querySelector('.avatar-overlay') as HTMLElement;
            if (overlay) overlay.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateX(-50%) scale(1)';
            e.currentTarget.style.boxShadow = '0 16px 40px rgba(0, 0, 0, 0.2), 0 8px 24px rgba(0, 0, 0, 0.1), 0 0 0 2px rgba(255, 255, 255, 0.8)';
            const overlay = e.currentTarget.querySelector('.avatar-overlay') as HTMLElement;
            if (overlay) overlay.style.opacity = '0';
          }}
        >
          {user.photoURL ? (
            <img
              src={user.photoURL}
              alt="Profile"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '50%'
              }}
            />
          ) : (
            <UserIcon style={{ width: '48px', height: '48px', color: 'rgba(255, 255, 255, 0.8)' }} />
          )}

          {/* Overlay on hover */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.3s ease',
            pointerEvents: 'none'
          }} className="avatar-overlay">
            <div style={{
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            }}>
              <CameraIcon style={{ width: '20px', height: '20px', color: '#ee4238' }} />
            </div>
          </div>


        </div>

        {/* Content Section */}
        <div style={contentStyle}>
          <h2 style={nameStyle}>{user.name}</h2>
          <p style={emailStyle}>{user.email}</p>

          {/* Enhanced Dark Background Section */}
          <div style={{
            background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 25%, #16213e 50%, #1a1a2e 75%, #0f0f23 100%)',
            margin: '0 -16px -16px -16px',
            padding: '32px 16px 32px 16px',
            borderRadius: '0 0 28px 28px',
            marginTop: '20px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Animated Background Pattern */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 20% 80%, rgba(255, 20, 147, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 107, 53, 0.1) 0%, transparent 50%)',
              animation: 'pulse 4s ease-in-out infinite alternate'
            }}></div>

            <div style={{
              ...levelBadgeStyle,
              marginTop: '24px',
              marginBottom: '28px',
              position: 'relative',
              zIndex: 2
            }}>
              {/* Stylish Badge Container */}
              <div style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px'
              }}>

                {/* Main Badge with Premium Design */}
                <div style={{
                  position: 'relative',
                  width: '80px',
                  height: '80px',
                  background: '#000000',
                  border: memberLevel.level === 'Master'
                    ? '3px solid rgba(255, 107, 53, 0.9)'
                    : '3px solid rgba(255, 20, 147, 0.9)',
                  clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: memberLevel.level === 'Master'
                    ? '0 0 30px rgba(255, 107, 53, 0.8), 0 0 60px rgba(255, 107, 53, 0.6)'
                    : '0 0 30px rgba(255, 20, 147, 0.6), 0 0 60px rgba(255, 20, 147, 0.3)'
                }}>


                  {/* Level Icon with Enhanced Styling - Only show if not locked */}
                  {memberLevel.iconSrc && (
                    <img src={memberLevel.iconSrc} alt={memberLevel.level}
                      style={{
                        width: '70px',
                        height: '70px',
                        borderRadius: '0px',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 2,
                        filter: memberLevel.level === 'Master'
                          ? 'drop-shadow(0 0 20px rgba(255, 107, 53, 0.8)), drop-shadow(0 0 40px rgba(255, 107, 53, 0.6)), drop-shadow(0 0 60px rgba(255, 107, 53, 0.4))'
                          : 'drop-shadow(0 0 12px rgba(255, 20, 147, 0.6))'
                      }} />
                  )}

                  {/* Locked State Icon */}
                  {memberLevel.level === 'Locked' && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 2,
                      fontSize: '40px',
                      color: '#666666',
                      textShadow: '0 0 10px rgba(102, 102, 102, 0.8)'
                    }}>
                      🔒
                    </div>
                  )}

                  {/* Animated Glow Effect (Separate Layer) - Only show if not locked */}
                  {memberLevel.level !== 'Locked' && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      zIndex: 1,
                      animation: memberLevel.level === 'Master'
                        ? 'fireGlow 2s ease-in-out infinite'
                        : 'hexGlow 3s ease-in-out infinite alternate',
                      pointerEvents: 'none'
                    }}></div>
                  )}

                  {/* Fire Effect for Master Level */}
                  {memberLevel.level === 'Master' && (
                    <>
                      {/* Main Fire Circle */}
                      <div style={{
                        position: 'absolute',
                        bottom: '-30px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '90px',
                        height: '90px',
                        borderRadius: '50%',
                        border: '3px solid #fff',
                        zIndex: 1,
                        animation: 'fireMove 4s ease-in-out infinite'
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: '15px',
                          left: '15px',
                          right: '15px',
                          bottom: '15px',
                          borderRadius: '50%',
                          border: '2px solid #fff',
                          boxShadow: '0 0 8px #fff, inset 0 0 8px #fff'
                        }}></div>
                      </div>

                      {/* Fire Flicker Effect */}
                      <div style={{
                        position: 'absolute',
                        bottom: '-25px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, #e63a32 0%, #ee4238 40%, transparent 70%)',
                        zIndex: 0,
                        animation: 'fireFlicker 2s ease-in-out infinite'
                      }}></div>

                      {/* Rising Fire Particles */}
                      <div style={{
                        position: 'absolute',
                        bottom: '-15px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: '#ff4500',
                        zIndex: 2,
                        animation: 'fireRise 3s ease-out infinite'
                      }}></div>

                      <div style={{
                        position: 'absolute',
                        bottom: '-10px',
                        left: 'calc(50% - 15px)',
                        transform: 'translateX(-50%)',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: '#ee4238',
                        zIndex: 2,
                        animation: 'fireRise 2.5s ease-out infinite 0.5s'
                      }}></div>

                      <div style={{
                        position: 'absolute',
                        bottom: '-12px',
                        left: 'calc(50% + 12px)',
                        transform: 'translateX(-50%)',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#d63a32',
                        zIndex: 2,
                        animation: 'fireRise 3.5s ease-out infinite 1s'
                      }}></div>
                    </>
                  )}
                </div>

                {/* Enhanced Stylish Rank Badge */}
                <div style={{
                  background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)',
                  border: '2px solid',
                  borderImage: 'linear-gradient(45deg, #ee4238, #d63a32, #ffa726, #ee4238) 1',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  boxShadow: '0 0 25px rgba(255, 107, 53, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                  position: 'relative',
                  overflow: 'hidden',
                  transform: 'translateY(0)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 0 35px rgba(255, 107, 53, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 0 25px rgba(255, 107, 53, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
                  }}>
                  {/* Animated Background Shine */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
                    animation: 'shimmer 3s ease-in-out infinite'
                  }}></div>

                  {/* Rank Text */}
                  <span style={{
                    color: '#ffffff',
                    fontSize: '11px',
                    fontWeight: '800',
                    textTransform: 'uppercase',
                    letterSpacing: '1.5px',
                    whiteSpace: 'nowrap',
                    textShadow: '0 0 15px rgba(255, 107, 53, 0.8), 0 2px 4px rgba(0, 0, 0, 0.5)',
                    background: 'linear-gradient(45deg, #ffffff, #ffd700, #ffffff)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 0 10px rgba(255, 107, 53, 0.6))'
                  }}>
                    {t(`profileHero.level.${getLevelKey(memberLevel.level)}`)} {t('profileHero.rank')}
                  </span>

                  {/* Corner Accents */}
                  <div style={{
                    position: 'absolute',
                    top: '2px',
                    left: '2px',
                    width: '6px',
                    height: '6px',
                    borderTop: '2px solid #ee4238',
                    borderLeft: '2px solid #ee4238',
                    borderRadius: '2px 0 0 0'
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    width: '6px',
                    height: '6px',
                    borderTop: '2px solid #ee4238',
                    borderRight: '2px solid #ee4238',
                    borderRadius: '0 2px 0 0'
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    bottom: '2px',
                    left: '2px',
                    width: '6px',
                    height: '6px',
                    borderBottom: '2px solid #ee4238',
                    borderLeft: '2px solid #ee4238',
                    borderRadius: '0 0 0 2px'
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    bottom: '2px',
                    right: '2px',
                    width: '6px',
                    height: '6px',
                    borderBottom: '2px solid #ee4238',
                    borderRight: '2px solid #ee4238',
                    borderRadius: '0 0 2px 0'
                  }}></div>
                </div>
              </div>
            </div>

            {/* Enhanced Test Controls */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '12px',
              marginTop: '24px',
              marginBottom: '20px',
              padding: '16px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              zIndex: 2
            }}>
            </div>

            {/* Enhanced Progress Section */}
            <div style={{
              marginTop: '20px',
              position: 'relative',
              zIndex: 2
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
                gap: '16px',
                padding: '20px',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(15px)'
              }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#ffffff',
                  lineHeight: '1.4',
                  textShadow: '0 0 15px rgba(255, 255, 255, 0.3)',
                  textAlign: 'center'
                }}>
                  {memberLevel.next ? t('profileHero.moreScansNeeded', { count: pointsToNext, next: t(`profileHero.level.${getLevelKey(memberLevel.next)}`) }) : t('profileHero.masterLevelAchieved')}
                </div>
                <div style={{
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <img
                    src={memberLevel.next ? `/${memberLevel.next === 'Platinum' ? 'PLATINAM' : memberLevel.next === 'Master' ? 'MASTER' : memberLevel.next === 'Diamond' ? 'DIAMOND' : memberLevel.next.toUpperCase()}.png` : '/GOLD.png'}
                    alt={memberLevel.next || 'Master'}
                    style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '0px',
                      filter: 'drop-shadow(0 0 25px rgba(255, 215, 0, 0.9)), drop-shadow(0 0 50px rgba(255, 215, 0, 0.7)), drop-shadow(0 0 75px rgba(255, 215, 0, 0.5))'
                    }}
                  />
                </div>
              </div>

              <div style={{
                width: '100%',
                height: '12px',
                background: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '8px',
                overflow: 'hidden',
                position: 'relative',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  height: '100%',
                  background: 'linear-gradient(90deg, #ee4238 0%, #d63a32 50%, #ffa726 100%)',
                  borderRadius: '8px',
                  width: `${Math.min(100, Math.max(0, progressPercent))}%`,
                  transition: 'width 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  boxShadow: '0 0 20px rgba(255, 107, 53, 0.5)'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)',
                    animation: 'shimmer 2.5s ease-in-out infinite'
                  }}></div>
                </div>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '16px',
                padding: '0 4px'
              }}>
                <div style={{
                  fontSize: '13px',
                  color: '#ffffff',
                  fontWeight: '600',
                  textShadow: '0 0 10px rgba(255, 255, 255, 0.3)'
                }}>
                  {points} {t('profileHero.points')}
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#ffffff',
                  fontWeight: '600',
                  textShadow: '0 0 10px rgba(255, 255, 255, 0.3)'
                }}>
                  {memberLevel.next ? memberLevel.required : '∞'} {t('profileHero.required')}
                </div>
              </div>
            </div>

            {/* Enhanced Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '16px',
              marginTop: '28px',
              position: 'relative',
              zIndex: 2
            }}>

              <button
                style={{
                  flex: 1,
                  padding: '16px 20px',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '16px',
                  color: '#ffffff',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                  textShadow: '0 0 10px rgba(255, 255, 255, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)';
                  e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)';
                  e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.2)';
                }}
                onClick={async () => {
                  try {
                    // Wait for lifetime earnings to load if still loading
                    if (lifetimeEarningsLoading) {
                      toast.loading('Loading earnings data...', { id: 'loading-earnings' });
                      // Wait a bit for the data to load
                      await new Promise(resolve => setTimeout(resolve, 500));
                      toast.dismiss('loading-earnings');
                    }

                    // Create a high-resolution canvas for better quality
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d', { alpha: false });
                    if (!ctx) {
                      toast.error('Canvas not supported');
                      return;
                    }

                    // 3x resolution for high quality (1800x1260)
                    const scale = 3;
                    const baseWidth = 600;
                    const baseHeight = 420;
                    canvas.width = baseWidth * scale;
                    canvas.height = baseHeight * scale;
                    ctx.scale(scale, scale);

                    // Create red and white split design
                    // Top half - White
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, baseWidth, 210);

                    // Bottom half - Red gradient
                    const gradient = ctx.createLinearGradient(0, 210, 0, baseHeight);
                    gradient.addColorStop(0, '#ee4238');
                    gradient.addColorStop(1, '#d63a32');
                    ctx.fillStyle = gradient;
                    ctx.fillRect(0, 210, baseWidth, 210);

                    // Load and add SCI Paints Bandhu Logo at top
                    const logo = new Image();
                    logo.crossOrigin = 'anonymous';
                    logo.src = '/SCI Paints Bandhu Logo.png';
                    await new Promise((resolve) => {
                      logo.onload = () => {
                        // Draw logo centered at top
                        const logoWidth = 120;
                        const logoHeight = (logo.height / logo.width) * logoWidth;
                        ctx.drawImage(logo, (600 - logoWidth) / 2, 20, logoWidth, logoHeight);
                        resolve(null);
                      };
                      logo.onerror = () => {
                        // If logo fails, show text instead
                        ctx.fillStyle = '#ee4238';
                        ctx.font = 'bold 24px Arial';
                        ctx.textAlign = 'center';
                        ctx.fillText('SCI PAINTS', 300, 45);
                        resolve(null);
                      };
                    });

                    // Add customer name in white section
                    ctx.fillStyle = '#1a1a1a';
                    ctx.font = 'bold 32px Arial';
                    ctx.textAlign = 'center';
                    ctx.fillText(user.name || 'Customer Name', 300, 110);

                    // Load and draw badge PNG image in white section
                    const loadBadgeImage = () => {
                      return new Promise((resolve) => {
                        if (memberLevel.level === 'Locked') {
                          // Draw lock icon for locked state
                          ctx.fillStyle = '#666666';
                          ctx.font = 'bold 50px Arial';
                          ctx.textAlign = 'center';
                          ctx.fillText('🔒', 300, 170);
                          resolve(null);
                        } else {
                          const img = new Image();
                          img.crossOrigin = 'anonymous';
                          img.onload = () => {
                            // Draw badge image in hexagonal container
                            ctx.save();

                            // Create hexagonal path
                            const centerX = 300;
                            const centerY = 170;
                            const radius = 35;
                            ctx.beginPath();
                            for (let i = 0; i < 6; i++) {
                              const angle = (i * Math.PI) / 3;
                              const x = centerX + radius * Math.cos(angle);
                              const y = centerY + radius * Math.sin(angle);
                              if (i === 0) ctx.moveTo(x, y);
                              else ctx.lineTo(x, y);
                            }
                            ctx.closePath();
                            ctx.clip();

                            // Draw the image
                            ctx.drawImage(img, centerX - radius, centerY - radius, radius * 2, radius * 2);
                            ctx.restore();

                            // Add hexagonal border with glow effect (same as main profile)
                            ctx.shadowColor = memberLevel.color;
                            ctx.shadowBlur = 20;
                            ctx.strokeStyle = memberLevel.color;
                            ctx.lineWidth = 3;
                            ctx.beginPath();
                            for (let i = 0; i < 6; i++) {
                              const angle = (i * Math.PI) / 3;
                              const x = centerX + (radius + 2) * Math.cos(angle);
                              const y = centerY + (radius + 2) * Math.sin(angle);
                              if (i === 0) ctx.moveTo(x, y);
                              else ctx.lineTo(x, y);
                            }
                            ctx.closePath();
                            ctx.stroke();
                            ctx.shadowBlur = 0;

                            resolve(null);
                          };
                          img.onerror = () => {
                            // Fallback to text if image fails
                            ctx.fillStyle = memberLevel.color;
                            ctx.font = 'bold 30px Arial';
                            ctx.textAlign = 'center';
                            ctx.fillText(memberLevel.level[0], 300, 205);
                            resolve(null);
                          };
                          img.src = memberLevel.iconSrc || '';
                        }
                      });
                    };

                    // Rank text box on red background
                    const rankBoxX = 300;
                    const rankBoxY = 240;
                    const rankBoxWidth = 200;
                    const rankBoxHeight = 38;

                    // Rank box background (white)
                    ctx.fillStyle = '#ffffff';
                    ctx.roundRect(rankBoxX - rankBoxWidth / 2, rankBoxY, rankBoxWidth, rankBoxHeight, 10);
                    ctx.fill();

                    // Rank box border (red)
                    ctx.strokeStyle = '#ffffff';
                    ctx.lineWidth = 3;
                    ctx.roundRect(rankBoxX - rankBoxWidth / 2, rankBoxY, rankBoxWidth, rankBoxHeight, 10);
                    ctx.stroke();

                    // Rank text (red)
                    ctx.fillStyle = '#ee4238';
                    ctx.font = 'bold 18px Arial';
                    ctx.textAlign = 'center';
                    ctx.fillText(`${memberLevel.level.toUpperCase()} RANK`, rankBoxX, rankBoxY + 25);

                    // Stats layout in two columns on red background
                    ctx.fillStyle = '#ffffff';
                    ctx.font = 'bold 15px Arial';
                    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
                    ctx.shadowBlur = 4;

                    // Left column
                    ctx.textAlign = 'left';
                    ctx.fillText(`💰 Earning: ₹${lifetimeEarnings || 0}`, 50, 310);
                    ctx.fillText(`📊 Points: ${points}`, 50, 340);

                    // Right column
                    ctx.textAlign = 'right';
                    ctx.fillText(`📅 Member: ${memberSince}d`, 550, 310);
                    ctx.fillText(`🎯 Next: ${memberLevel.next || 'MAX'}`, 550, 340);
                    ctx.shadowBlur = 0;

                    // Load badge image
                    await loadBadgeImage();

                    // Add decorative line separator
                    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(50, 365);
                    ctx.lineTo(550, 365);
                    ctx.stroke();

                    // Add footer
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
                    ctx.font = 'bold 12px Arial';
                    ctx.textAlign = 'center';
                    ctx.fillText('🎨 SCI PAINTS BANDHU • YOUR REWARDS PARTNER 🎨', 300, 390);

                    // Convert to high-quality blob and show preview
                    setTimeout(() => {
                      canvas.toBlob((blob) => {
                        if (!blob) {
                          toast.error('Failed to create image');
                          return;
                        }
                        const url = URL.createObjectURL(blob);
                        setShareImageUrl(url);
                        setShowSharePreview(true);
                      }, 'image/png', 1.0); // Maximum quality PNG
                    }, 200); // Longer delay for high-res rendering
                  } catch (error) {
                    console.error('Share failed:', error);
                    toast.error('Failed to create profile card');
                  }
                }}
              >
                {t('profileHero.shareButton')} →
              </button>
              <button
                style={{
                  flex: 1,
                  padding: '16px 20px',
                  background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
                  border: '2px solid rgba(255, 107, 53, 0.3)',
                  borderRadius: '16px',
                  color: '#ffffff',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 8px 32px rgba(255, 107, 53, 0.3), 0 0 20px rgba(255, 107, 53, 0.2)',
                  textShadow: '0 0 10px rgba(255, 255, 255, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #e03830 0%, #ee4238 100%)';
                  e.currentTarget.style.border = '2px solid rgba(255, 107, 53, 0.5)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(255, 107, 53, 0.4), 0 0 30px rgba(255, 107, 53, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)';
                  e.currentTarget.style.border = '2px solid rgba(255, 107, 53, 0.3)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(255, 107, 53, 0.3), 0 0 20px rgba(255, 107, 53, 0.2)';
                }}
                onClick={() => setShowProfileModal(true)}
              >
                {t('profileHero.viewProfileButton')} →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Compact Quick Stats */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: '16px',
        padding: '16px',
        backdropFilter: 'blur(15px)',
        marginTop: '10px',
        marginBottom: '10px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '12px'
        }}>
          {/* Level */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '12px 8px',
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            transition: 'all 0.2s ease',
            cursor: 'pointer'
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
            <div style={{
              fontSize: '20px',
              marginBottom: '6px',
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
            }}>
              {memberLevel.level === 'Locked' ? '🔒' :
                memberLevel.level === 'Bronze' ? '🥉' :
                  memberLevel.level === 'Gold' ? '🥇' :
                    memberLevel.level === 'Platinum' ? '🥈' :
                      memberLevel.level === 'Diamond' ? '💎' :
                        memberLevel.level === 'Master' ? '🔥' : '🏆'}
            </div>
            <div style={{
              fontSize: '14px',
              fontWeight: '700',
              color: memberLevel.level === 'Locked' ? '#666666' :
                memberLevel.level === 'Bronze' ? '#cd7f32' :
                  memberLevel.level === 'Gold' ? '#ffd700' :
                    memberLevel.level === 'Platinum' ? '#e5e4e2' :
                      memberLevel.level === 'Diamond' ? '#b9f2ff' :
                        memberLevel.level === 'Master' ? '#ee4238' : '#1a1a1a',
              marginBottom: '4px'
            }}>{memberLevel.level}</div>
            <div style={{
              fontSize: '10px',
              color: '#6b7280',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>Level</div>
          </div>

          {/* Earned */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '12px 8px',
            borderRadius: '12px',
            background: 'rgba(255, 215, 0, 0.08)',
            border: '1px solid rgba(255, 215, 0, 0.15)',
            transition: 'all 0.2s ease',
            cursor: 'pointer'
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 215, 0, 0.15)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 215, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
            <div style={{
              fontSize: '20px',
              marginBottom: '6px',
              filter: 'drop-shadow(0 2px 4px rgba(255, 215, 0, 0.3))'
            }}>💰</div>
            <div style={{
              fontSize: '14px',
              fontWeight: '700',
              color: '#018328',
              marginBottom: '4px',
              textShadow: '0 0 8px rgba(255, 215, 0, 0.4)'
            }}>₹{lifetimeEarnings || 0}</div>
            <div style={{
              fontSize: '10px',
              color: '#6b7280',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>Earned</div>
          </div>

          {/* Member */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '12px 8px',
            borderRadius: '12px',
            background: 'rgba(74, 222, 128, 0.08)',
            border: '1px solid rgba(74, 222, 128, 0.15)',
            transition: 'all 0.2s ease',
            cursor: 'pointer'
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(74, 222, 128, 0.15)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(74, 222, 128, 0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
            <div style={{
              fontSize: '20px',
              marginBottom: '6px',
              filter: 'drop-shadow(0 2px 4px rgba(74, 222, 128, 0.3))'
            }}>👤</div>
            <div style={{
              fontSize: '14px',
              fontWeight: '700',
              color: '#4ade80',
              marginBottom: '4px',
              textShadow: '0 0 8px rgba(74, 222, 128, 0.4)'
            }}>{memberSince}d</div>
            <div style={{
              fontSize: '10px',
              color: '#6b7280',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>Member</div>
          </div>

          {/* Scans */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '12px 8px',
            borderRadius: '12px',
            background: 'rgba(96, 165, 250, 0.08)',
            border: '1px solid rgba(96, 165, 250, 0.15)',
            transition: 'all 0.2s ease',
            cursor: 'pointer'
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(96, 165, 250, 0.15)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(96, 165, 250, 0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}>
            <div style={{
              fontSize: '20px',
              marginBottom: '6px',
              filter: 'drop-shadow(0 2px 4px rgba(96, 165, 250, 0.3))'
            }}>📱</div>
            <div style={{
              fontSize: '14px',
              fontWeight: '700',
              color: '#60a5fa',
              marginBottom: '4px',
              textShadow: '0 0 8px rgba(96, 165, 250, 0.4)'
            }}>{points}</div>
            <div style={{
              fontSize: '10px',
              color: '#6b7280',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>Scans</div>
          </div>
        </div>
      </div>



      {/* Enhanced Settings */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '24px',
        padding: '8px',
        backdropFilter: 'blur(20px)',
        marginBottom: '24px'
      }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px 20px',
            borderRadius: '16px',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            margin: '4px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.15)';
            e.currentTarget.style.transform = 'translateX(4px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.08)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
          onClick={handleEditProfile}
        >
          <UserIcon style={{ width: '24px', height: '24px', color: '#60a5fa', marginRight: '16px' }} />
          <span style={{ color: '#1a1a1a', fontSize: '15px', fontWeight: '600', flex: 1 }}>{t('profile.editProfile.title')}</span>
          <ChevronRightIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
        </div>

        {/* KYC Details */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px 20px',
            borderRadius: '16px',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            margin: '4px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.15)';
            e.currentTarget.style.transform = 'translateX(4px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.08)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
          onClick={handleOpenKyc}
        >
          <ShieldCheckIcon style={{ width: '24px', height: '24px', color: '#ef4444', marginRight: '16px' }} />
          <span style={{ color: '#1a1a1a', fontSize: '15px', fontWeight: '600', flex: 1 }}>{t('profile.kyc.title')}</span>
          <ChevronRightIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px 20px',
            borderRadius: '16px',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            margin: '4px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.15)';
            e.currentTarget.style.transform = 'translateX(4px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.08)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
          onClick={handlePaymentInfo}
        >
          <CreditCardIcon style={{ width: '24px', height: '24px', color: '#4ade80', marginRight: '16px' }} />
          <span style={{ color: '#1a1a1a', fontSize: '15px', fontWeight: '600', flex: 1 }}>{t('profile.paymentInfo')}</span>
          <ChevronRightIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: screenWidth < 360 ? '12px 14px' : screenWidth < 414 ? '14px 16px' : '16px 20px',
            borderRadius: '16px',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            margin: '4px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.15)';
            e.currentTarget.style.transform = 'translateX(4px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.08)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
          onClick={() => {
            // Always open modal; inside modal we decide read-only vs input
            setShowDealerModal(true);
          }}
        >
          <BuildingStorefrontIcon style={{
            width: screenWidth < 360 ? '20px' : screenWidth < 414 ? '22px' : '24px',
            height: screenWidth < 360 ? '20px' : screenWidth < 414 ? '22px' : '24px',
            color: user?.dealerCode ? '#10b981' : '#ee4238',
            marginRight: screenWidth < 360 ? '12px' : screenWidth < 414 ? '14px' : '16px'
          }} />
          <span style={{
            color: '#1a1a1a',
            fontSize: screenWidth < 360 ? '13px' : screenWidth < 414 ? '14px' : '15px',
            fontWeight: '600',
            flex: 1
          }}>
            {user?.dealerCode ? t('profile.myDealer') : t('profile.addDealer')}
          </span>
          {user?.dealerCode ? (
            <>
              {dealerInfo ? (
                <div style={{ textAlign: 'right', maxWidth: screenWidth < 360 ? '110px' : screenWidth < 414 ? '130px' : '160px' }}>
                  <div style={{
                    fontSize: screenWidth < 360 ? '10px' : '11px',
                    fontWeight: 600,
                    color: '#10b981',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {dealerInfo.businessName || dealerInfo.name}
                  </div>
                  {dealerInfo.phone && (
                    <div style={{
                      fontSize: '10px',
                      color: '#6b7280',
                      marginTop: '2px'
                    }}>
                      {dealerInfo.phone}
                    </div>
                  )}
                </div>
              ) : (
                <span style={{
                  fontSize: screenWidth < 360 ? '10px' : screenWidth < 414 ? '11px' : '12px',
                  color: '#10b981',
                  fontWeight: '600',
                  wordBreak: 'break-all',
                  maxWidth: screenWidth < 360 ? '80px' : screenWidth < 414 ? '100px' : 'none',
                  overflow: screenWidth < 360 ? 'hidden' : 'visible',
                  textOverflow: screenWidth < 360 ? 'ellipsis' : 'clip'
                }}>
                  {user.dealerCode}
                </span>
              )}
            </>
          ) : (
            <ChevronRightIcon style={{
              width: screenWidth < 360 ? '18px' : screenWidth < 414 ? '19px' : '20px',
              height: screenWidth < 360 ? '18px' : screenWidth < 414 ? '19px' : '20px',
              color: '#6b7280'
            }} />
          )}
        </div>


        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px 20px',
            borderRadius: '16px',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            margin: '4px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.15)';
            e.currentTarget.style.transform = 'translateX(4px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.08)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
          onClick={() => toast.success('Privacy settings feature coming soon!')}
        >
          <ShieldCheckIcon style={{ width: '24px', height: '24px', color: '#a78bfa', marginRight: '16px' }} />
          <span style={{ color: '#1a1a1a', fontSize: '15px', fontWeight: '600', flex: 1 }}>{t('profile.privacySettings')}</span>
          <ChevronRightIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px 20px',
            borderRadius: '16px',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            margin: '4px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.15)';
            e.currentTarget.style.transform = 'translateX(4px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.08)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
          onClick={() => setShowAppPrefModal(true)}
        >
          <CogIcon style={{ width: '24px', height: '24px', color: '#f59e0b', marginRight: '16px' }} />
          <span style={{ color: '#1a1a1a', fontSize: '15px', fontWeight: '600', flex: 1 }}>{t('profile.appPreferences')}</span>
          <ChevronRightIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
        </div>

        {/* Download App Button */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px 20px',
            borderRadius: '16px',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            margin: '4px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.15)';
            e.currentTarget.style.transform = 'translateX(4px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.08)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
          onClick={() => {
            // Check if PWA install prompt is available
            const promptEvent = (window as any).deferredPrompt;
            if (promptEvent) {
              promptEvent.prompt();
              promptEvent.userChoice.then((choiceResult: any) => {
                if (choiceResult.outcome === 'accepted') {
                  console.log('User accepted the install prompt');
                }
                (window as any).deferredPrompt = null;
              });
            } else {
              // Fallback instructions if already installed or not supported
              const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
              if (isIOS) {
                toast.custom((t) => (
                  <div style={{
                    background: 'white',
                    padding: '16px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    zIndex: 10001
                  }}>
                    <div style={{ fontSize: '24px' }}>📲</div>
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#1a1a1a' }}>Install App</div>
                      <div style={{ fontSize: '14px', color: '#666' }}>Tap 'Share' then 'Add to Home Screen'</div>
                    </div>
                  </div>
                ), { duration: 5000 });
              } else {
                toast.success('App is already installed or check browser menu to install');
              }
            }
          }}
        >
          <ArrowDownTrayIcon style={{ width: '24px', height: '24px', color: '#10b981', marginRight: '16px' }} />
          <span style={{ color: '#1a1a1a', fontSize: '15px', fontWeight: '600', flex: 1 }}>Download App</span>
          <ChevronRightIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px 20px',
            borderRadius: '16px',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            margin: '4px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.15)';
            e.currentTarget.style.transform = 'translateX(4px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.08)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
          onClick={() => toast.success('Help center feature coming soon!')}
        >
          <QuestionMarkCircleIcon style={{ width: '24px', height: '24px', color: '#06b6d4', marginRight: '16px' }} />
          <span style={{ color: '#1a1a1a', fontSize: '15px', fontWeight: '600', flex: 1 }}>{t('profile.helpCenter')}</span>
          <ChevronRightIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px 20px',
            borderRadius: '16px',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            margin: '4px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
            e.currentTarget.style.border = '1px solid rgba(239, 68, 68, 0.3)';
            e.currentTarget.style.transform = 'translateX(4px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
            e.currentTarget.style.border = '1px solid rgba(239, 68, 68, 0.2)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
          onClick={handleLogout}
        >
          <ArrowRightOnRectangleIcon style={{ width: '24px', height: '24px', color: '#ef4444', marginRight: '16px' }} />
          <span style={{ color: '#ef4444', fontSize: '15px', fontWeight: '600', flex: 1 }}>{t('profile.signOut')}</span>
        </div>
      </div>

      {/* Photo Modal */}
      {showPhotoModal && (
        <div style={modalOverlayStyle} onClick={() => setShowPhotoModal(false)}>
          <div style={{
            ...modalContentStyle,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.5)',
            maxWidth: '400px',
            padding: '32px',
            position: 'relative'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              onClick={() => setShowPhotoModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                border: 'none',
                background: 'rgba(0, 0, 0, 0.05)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'rotate(90deg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.transform = 'rotate(0deg)';
              }}
            >
              <span style={{ fontSize: '20px', color: '#666' }}>×</span>
            </button>

            {/* Title with Icon */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '24px',
              gap: '12px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(238, 66, 56, 0.3)'
              }}>
                <CameraIcon style={{ width: '24px', height: '24px', color: 'white' }} />
              </div>
              <h3 style={{
                fontSize: '22px',
                fontWeight: '700',
                color: '#1a1a1a',
                margin: 0
              }}>
                Profile Picture
              </h3>
            </div>

            {/* Current Photo Preview */}
            {user.photoURL && (
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                overflow: 'hidden',
                margin: '0 auto 24px',
                border: '4px solid #f0f0f0',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                position: 'relative'
              }}>
                <img
                  src={user.photoURL}
                  alt="Current Profile"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            )}

            {/* Buttons Container */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <label
                htmlFor="photoUpload"
                style={{
                  ...modalPrimaryButtonStyle,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: 0,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(238, 66, 56, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(238, 66, 56, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(238, 66, 56, 0.3)';
                }}
              >
                <CameraIcon style={{ width: '20px', height: '20px' }} />
                {user.photoURL ? 'Change Photo' : 'Add Photo'}
                <input
                  id="photoUpload"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    if (file) handlePhotoSelect(file);
                  }}
                />
              </label>

              {user.photoURL && (
                <button
                  style={{
                    ...modalSecondaryButtonStyle,
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    marginBottom: 0
                  }}
                  onClick={handleRemovePhoto}
                  disabled={photoUploading}
                  onMouseEnter={(e) => {
                    if (!photoUploading) {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!photoUploading) {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {photoUploading ? 'Removing...' : 'Remove Photo'}
                </button>
              )}

              <button
                style={{
                  ...modalSecondaryButtonStyle,
                  marginBottom: 0,
                  background: '#f8f9fa',
                  color: '#666'
                }}
                onClick={() => setShowPhotoModal(false)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e9ecef';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f8f9fa';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Cancel
              </button>
            </div>

            {/* Info Text */}
            <p style={{
              fontSize: '12px',
              color: '#999',
              marginTop: '16px',
              marginBottom: 0
            }}>
              Supported formats: JPG, PNG, GIF (Max 5MB)
            </p>
          </div>
        </div>
      )}

      {/* Photo Preview Modal */}
      {photoPreview && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: '#1a1a1a' }}>
              Preview Photo
            </h3>

            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              overflow: 'hidden',
              margin: '0 auto 20px',
              border: '4px solid #f0f0f0'
            }}>
              <img
                src={photoPreview}
                alt="Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>

            <button
              style={modalPrimaryButtonStyle}
              onClick={handlePhotoUpload}
              disabled={photoUploading}
            >
              {photoUploading ? 'Uploading...' : 'Upload Photo'}
            </button>

            <button
              style={modalSecondaryButtonStyle}
              onClick={() => setPhotoPreview(null)}
              disabled={photoUploading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {photoUploading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f0f0f0',
              borderTop: '4px solid #ee4238',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }}></div>
            <p style={{ color: '#666', fontSize: '16px', fontWeight: '500' }}>
              Uploading photo...
            </p>
          </div>
        </div>
      )}

      {/* Profile View Modal */}
      {showProfileModal && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowProfileModal(false);
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(15px)',
            padding: screenWidth < 768 ? '16px 16px calc(16px + 80px) 16px' : '20px 20px calc(20px + 80px) 20px',
            overflowY: 'auto',
            paddingTop: screenWidth < 768 ? '16px' : '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
              borderRadius: '24px',
              padding: screenWidth < 768 ? '24px' : '40px',
              maxWidth: screenWidth < 768 ? '100%' : screenWidth < 1024 ? '650px' : '750px',
              width: '100%',
              maxHeight: screenWidth < 768 ? 'calc(100vh - 120px)' : 'calc(100vh - 140px)',
              overflowY: 'auto',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              position: 'relative',
              marginTop: screenWidth < 768 ? '0' : '0',
              marginBottom: screenWidth < 768 ? '0' : '0'
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowProfileModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                color: '#ffffff',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              ✕
            </button>

            {/* Header */}
            <div style={{
              textAlign: 'center',
              marginBottom: '32px'
            }}>
              <h2 style={{
                color: '#ffffff',
                fontSize: '28px',
                fontWeight: '800',
                margin: '0 0 8px 0',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
              }}>🏆 Profile Details</h2>
              <div style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                fontWeight: '500'
              }}>Complete profile information</div>
            </div>

            {/* Profile Avatar */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '24px'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: user.photoURL ? `url(${user.photoURL})` : 'linear-gradient(135deg, #ee4238, #d63a32)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                border: '4px solid rgba(255, 255, 255, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px',
                color: '#ffffff',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
              }}>
                {!user.photoURL && (user.name?.[0]?.toUpperCase() || '👤')}
              </div>
            </div>

            {/* User Info */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '24px',
              backdropFilter: 'blur(10px)'
            }}>
              <h3 style={{
                color: '#ffffff',
                fontSize: '24px',
                fontWeight: '700',
                textAlign: 'center',
                margin: '0 0 16px 0',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
              }}>{user.name || 'User Name'}</h3>

              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '16px'
              }}>
                <div style={{
                  background: `linear-gradient(135deg, ${memberLevel.color}, ${memberLevel.color}80)`,
                  padding: '8px 16px',
                  borderRadius: '20px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                }}>
                  {memberLevel.level} LEVEL
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>📱</div>
                <div style={{ color: '#ffffff', fontSize: '20px', fontWeight: '700' }}>{points}</div>
                <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px' }}>Total Points</div>
              </div>

              <div style={{
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>💰</div>
                <div style={{ color: '#FFD700', fontSize: '20px', fontWeight: '700' }}>₹{lifetimeEarnings || 0}</div>
                <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px' }}>Lifetime Earnings</div>
              </div>

              <div style={{
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>👤</div>
                <div style={{ color: '#4ade80', fontSize: '20px', fontWeight: '700' }}>{memberSince}d</div>
                <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px' }}>Member Since</div>
              </div>

              <div style={{
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🎯</div>
                <div style={{ color: '#60a5fa', fontSize: '20px', fontWeight: '700' }}>{memberLevel.pointsToNext || 0}</div>
                <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px' }}>Points to Next</div>
              </div>
            </div>

            {/* Contact Info */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '24px',
              backdropFilter: 'blur(10px)'
            }}>
              <h4 style={{
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: '700',
                marginBottom: '16px',
                textAlign: 'center'
              }}>📧 Contact Information</h4>

              <div style={{ marginBottom: '12px' }}>
                <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px', marginBottom: '4px' }}>Email</div>
                <div style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>{user.email}</div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px', marginBottom: '4px' }}>Phone</div>
                <div style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>{user.phone || 'Not provided'}</div>
              </div>

              <div>
                <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px', marginBottom: '4px' }}>Location</div>
                <div style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>
                  {user.city || 'Not specified'}, {user.state || 'Not specified'}
                </div>
                <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: '500', marginTop: '2px' }}>
                  {user.district || 'Not specified'}, {user.pinCode || 'Not specified'}
                </div>
              </div>
            </div>

            {/* Progress Info */}
            {memberLevel.next && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '20px',
                backdropFilter: 'blur(10px)'
              }}>
                <h4 style={{
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '700',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>🚀 Progress to {memberLevel.next}</h4>

                <div style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  height: '12px',
                  marginBottom: '12px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    background: 'linear-gradient(90deg, #ee4238, #d63a32)',
                    height: '100%',
                    width: `${progressPercent}%`,
                    borderRadius: '12px',
                    transition: 'width 1s ease'
                  }}></div>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '12px'
                }}>
                  <span>{points} {t('profileHero.points')}</span>
                  <span>{memberLevel.required} required</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Share Preview Modal */}
      {showSharePreview && shareImageUrl && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(20px)',
          padding: '20px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            borderRadius: '24px',
            padding: '32px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.8)',
            border: '2px solid rgba(255, 255, 255, 0.1)',
            position: 'relative'
          }}>
            {/* Close Button */}
            <button
              onClick={() => {
                setShowSharePreview(false);
                if (shareImageUrl) {
                  URL.revokeObjectURL(shareImageUrl);
                  setShareImageUrl(null);
                }
              }}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                color: '#ffffff',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                zIndex: 2
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              ✕
            </button>

            {/* Header */}
            <div style={{
              textAlign: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{
                color: '#ffffff',
                fontSize: '24px',
                fontWeight: '800',
                margin: '0 0 8px 0',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
              }}>📤 Share Profile Preview</h2>
              <div style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '14px',
                fontWeight: '500'
              }}>Preview your profile card before sharing</div>
            </div>

            {/* Image Preview */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '24px',
              position: 'relative'
            }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '16px',
                padding: '16px',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}>
                <img
                  src={shareImageUrl}
                  alt="Profile Card Preview"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)'
                  }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(shareImageUrl);
                    const blob = await response.blob();
                    const file = new File([blob], `${user.name}_Profile.png`, { type: 'image/png' });

                    if (navigator.share && navigator.canShare({ files: [file] })) {
                      await navigator.share({
                        title: `${user.name}'s Profile - SciPaint`,
                        text: `Check out my SciPaint profile! Level: ${memberLevel.level}`,
                        files: [file]
                      });
                      toast.success('Profile card shared successfully!');
                    } else {
                      // Fallback: download the image
                      const a = document.createElement('a');
                      a.href = shareImageUrl;
                      a.download = `${user.name}_Profile.png`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      toast.success('Profile card downloaded!');
                    }

                    // Close modal and cleanup
                    setShowSharePreview(false);
                    if (shareImageUrl) {
                      URL.revokeObjectURL(shareImageUrl);
                      setShareImageUrl(null);
                    }
                  } catch (error) {
                    console.error('Share failed:', error);
                    toast.error('Failed to share profile card');
                  }
                }}
                style={{
                  padding: '16px 32px',
                  background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
                  border: 'none',
                  borderRadius: '16px',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 8px 24px rgba(255, 107, 53, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(255, 107, 53, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 107, 53, 0.4)';
                }}
              >
                📤 Share Now
              </button>

              <button
                onClick={() => {
                  // Download the image
                  const a = document.createElement('a');
                  a.href = shareImageUrl;
                  a.download = `${user.name}_Profile.png`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  toast.success('Profile card downloaded!');

                  // Close modal and cleanup
                  setShowSharePreview(false);
                  if (shareImageUrl) {
                    URL.revokeObjectURL(shareImageUrl);
                    setShareImageUrl(null);
                  }
                }}
                style={{
                  padding: '16px 32px',
                  background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                  border: 'none',
                  borderRadius: '16px',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 8px 24px rgba(96, 165, 250, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(96, 165, 250, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(96, 165, 250, 0.4)';
                }}
              >
                💾 Download
              </button>
            </div>

            {/* Info Text */}
            <div style={{
              textAlign: 'center',
              marginTop: '20px',
              padding: '16px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <p style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                margin: 0,
                lineHeight: '1.5'
              }}>
                💡 <strong>Tip:</strong> Use "Share Now" to share directly to social media or messaging apps.
                Use "Download" to save the image to your device.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Info Modal */}
      {showPaymentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(8px)',
          padding: screenWidth < 768 ? '16px 16px calc(16px + 80px) 16px' : '40px 40px calc(40px + 80px) 40px',
          overflowY: 'auto'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '20px',
            padding: screenWidth < 768 ? '24px' : '32px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: screenWidth < 768 ? 'calc(100vh - 120px)' : 'calc(100vh - 160px)',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            position: 'relative',
            marginTop: screenWidth < 768 ? '20px' : '40px'
          }}>
            {/* Close Button */}
            <button
              onClick={() => setShowPaymentModal(false)}
              style={{
                position: 'absolute',
                top: screenWidth < 768 ? '16px' : '20px',
                right: screenWidth < 768 ? '16px' : '20px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50%',
                width: screenWidth < 768 ? '36px' : '40px',
                height: screenWidth < 768 ? '36px' : '40px',
                color: '#ffffff',
                fontSize: screenWidth < 768 ? '18px' : '20px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                zIndex: 10,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                e.currentTarget.style.transform = 'scale(1.1) rotate(90deg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
              }}
            >
              ✕
            </button>

            {/* Header with Brand Theme */}
            <div style={{
              background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
              borderRadius: screenWidth < 768 ? '16px 16px 0 0' : '20px 20px 0 0',
              padding: screenWidth < 768 ? '24px 20px' : '32px 24px',
              marginTop: screenWidth < 768 ? '-24px' : '-24px',
              marginLeft: screenWidth < 768 ? '-24px' : '-24px',
              marginRight: screenWidth < 768 ? '-24px' : '-24px',
              marginBottom: screenWidth < 768 ? '20px' : '28px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Decorative Elements */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: screenWidth < 768 ? '120px' : '150px',
                height: screenWidth < 768 ? '120px' : '150px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
                pointerEvents: 'none'
              }} />
              <div style={{
                position: 'absolute',
                bottom: '-30px',
                left: '-30px',
                width: screenWidth < 768 ? '80px' : '100px',
                height: screenWidth < 768 ? '80px' : '100px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '50%',
                pointerEvents: 'none'
              }} />

              <div style={{ position: 'relative', textAlign: 'center' }}>
                <div style={{
                  width: screenWidth < 768 ? '48px' : '56px',
                  height: screenWidth < 768 ? '48px' : '56px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
                }}>
                  <CreditCardIcon style={{ width: screenWidth < 768 ? '24px' : '28px', height: screenWidth < 768 ? '24px' : '28px', color: '#ffffff' }} />
                </div>
                <h2 style={{
                  color: '#ffffff',
                  fontSize: screenWidth < 768 ? '22px' : '26px',
                  fontWeight: '700',
                  margin: '0 0 8px 0',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                }}>{t('profile.paymentInfoTitle')}</h2>
                <div style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: screenWidth < 768 ? '12px' : '14px',
                  fontWeight: '500',
                  textShadow: '0 1px 4px rgba(0, 0, 0, 0.1)'
                }}>{t('profile.paymentInfoSubtitle')}</div>
              </div>
            </div>

            {/* Payment Details View */}
            {!isEditingPayment && (
              <div>
                {(() => {
                  console.log('👁️ View Mode Debug:', {
                    paymentMethod: paymentFormData.paymentMethod,
                    upiId: paymentFormData.upiId,
                    bankName: paymentFormData.bankName,
                    accountNumber: paymentFormData.accountNumber,
                    isEditingPayment
                  });
                  return null;
                })()}
                {/* Current Payment Method Display */}
                <div style={{ marginBottom: '24px' }}>
                  {/* UPI Section */}
                  {paymentFormData.upiId && paymentFormData.upiId.trim() !== '' && (
                    <div style={{
                      background: 'linear-gradient(135deg, #fff5f5 0%, #ffffff 100%)',
                      borderRadius: '16px',
                      padding: '20px',
                      border: '2px solid #fee2e2',
                      marginBottom: '16px',
                      boxShadow: '0 4px 12px rgba(238, 66, 56, 0.08)'
                    }}>
                      <div style={{
                        color: '#495057',
                        fontSize: '14px',
                        fontWeight: '600',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        💳 {t('profile.payment.upiIdLabel')}
                        <span style={{
                          background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
                          color: '#ffffff',
                          fontSize: '10px',
                          padding: '3px 10px',
                          borderRadius: '12px',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          boxShadow: '0 2px 8px rgba(238, 66, 56, 0.3)'
                        }}>
                          {paymentFormData.paymentMethod === 'upi' ? t('profile.payment.active') : t('profile.payment.saved')}
                        </span>
                      </div>

                      <div style={{
                        color: '#6c757d',
                        fontSize: '12px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {t('profile.payment.upiIdLabel')}
                      </div>
                      <div style={{
                        color: '#1a1a1a',
                        fontSize: '16px',
                        fontWeight: '600',
                        padding: '12px 14px',
                        background: '#ffffff',
                        borderRadius: '10px',
                        border: '1px solid #e9ecef'
                      }}>
                        {paymentFormData.upiId}
                      </div>
                    </div>
                  )}

                  {/* Bank Account Section */}
                  {paymentFormData.bankName && paymentFormData.bankName.trim() !== '' && (
                    <div style={{
                      background: 'linear-gradient(135deg, #fff5f5 0%, #ffffff 100%)',
                      borderRadius: '16px',
                      padding: '20px',
                      border: '2px solid #fee2e2',
                      boxShadow: '0 4px 12px rgba(238, 66, 56, 0.08)'
                    }}>
                      <div style={{
                        color: '#495057',
                        fontSize: '14px',
                        fontWeight: '600',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        🏦 {t('profile.payment.bankOption')}
                        <span style={{
                          background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
                          color: '#ffffff',
                          fontSize: '10px',
                          padding: '3px 10px',
                          borderRadius: '12px',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          boxShadow: '0 2px 8px rgba(238, 66, 56, 0.3)'
                        }}>
                          {paymentFormData.paymentMethod === 'bank' ? t('profile.payment.active') : t('profile.payment.saved')}
                        </span>
                      </div>

                      {/* Bank Account Details */}
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          color: '#6c757d',
                          fontSize: '12px',
                          fontWeight: '600',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('profile.payment.accountHolder')}
                        </div>
                        <div style={{
                          color: '#1a1a1a',
                          fontSize: '16px',
                          fontWeight: '600',
                          padding: '12px 14px',
                          background: '#ffffff',
                          borderRadius: '10px',
                          border: '1px solid #e9ecef'
                        }}>
                          {paymentFormData.accountHolderName || t('profile.payment.notSet')}
                        </div>
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          color: '#6c757d',
                          fontSize: '12px',
                          fontWeight: '600',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('profile.payment.bankName')}
                        </div>
                        <div style={{
                          color: '#1a1a1a',
                          fontSize: '16px',
                          fontWeight: '600',
                          padding: '12px 14px',
                          background: '#ffffff',
                          borderRadius: '10px',
                          border: '1px solid #e9ecef'
                        }}>
                          {paymentFormData.bankName || t('profile.payment.notSet')}
                        </div>
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          color: '#6c757d',
                          fontSize: '12px',
                          fontWeight: '600',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('profile.payment.accountNumber')}
                        </div>
                        <div style={{
                          color: '#1a1a1a',
                          fontSize: '16px',
                          fontWeight: '600',
                          padding: '12px 14px',
                          background: '#ffffff',
                          borderRadius: '10px',
                          border: '1px solid #e9ecef'
                        }}>
                          {paymentFormData.accountNumber ? `****${paymentFormData.accountNumber.slice(-4)}` : t('profile.payment.notSet')}
                        </div>
                      </div>

                      <div>
                        <div style={{
                          color: '#6c757d',
                          fontSize: '12px',
                          fontWeight: '600',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('profile.payment.ifsc')}
                        </div>
                        <div style={{
                          color: '#1a1a1a',
                          fontSize: '16px',
                          fontWeight: '600',
                          padding: '12px 14px',
                          background: '#ffffff',
                          borderRadius: '10px',
                          border: '1px solid #e9ecef'
                        }}>
                          {paymentFormData.ifscCode || t('profile.payment.notSet')}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* No Payment Methods */}
                  {(!paymentFormData.upiId || paymentFormData.upiId.trim() === '') &&
                    (!paymentFormData.bankName || paymentFormData.bankName.trim() === '') && (
                      <div style={{
                        background: '#f8f9fa',
                        borderRadius: '16px',
                        padding: '20px',
                        border: '1px solid #e9ecef'
                      }}>
                        <div style={{
                          color: '#6c757d',
                          fontSize: '14px',
                          textAlign: 'center',
                          padding: '20px',
                          background: '#ffffff',
                          borderRadius: '10px',
                          border: '1px solid #e9ecef'
                        }}>
                          No payment method configured
                        </div>
                      </div>
                    )}
                </div>

                {/* Cooldown Warning Banner */}
                {(() => {
                  if (user?.paymentChangeRequest?.submittedAt) {
                    const lastSubmit = user.paymentChangeRequest.submittedAt;
                    const daysSinceLastSubmit = Math.floor((Date.now() - lastSubmit.getTime()) / (1000 * 60 * 60 * 24));
                    if (daysSinceLastSubmit < 60) {
                      const daysLeft = 60 - daysSinceLastSubmit;
                      return (
                        <div style={{
                          background: '#fef3c7',
                          border: '2px solid #fbbf24',
                          borderRadius: '12px',
                          padding: '16px',
                          marginBottom: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <div style={{ fontSize: '24px' }}>⏳</div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              color: '#92400e',
                              fontWeight: '700',
                              fontSize: '14px',
                              marginBottom: '4px'
                            }}>
                              {t('profile.payment.cooldownActive')}
                            </div>
                            <div style={{
                              color: '#78350f',
                              fontSize: '13px'
                            }}
                              dangerouslySetInnerHTML={{
                                __html: daysLeft > 1
                                  ? t('profile.payment.cooldownMessagePlural', { days: daysLeft })
                                  : t('profile.payment.cooldownMessage', { days: daysLeft })
                              }}
                            />
                          </div>
                        </div>
                      );
                    }
                  }
                  return null;
                })()}

                {/* Action Button for View Mode */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  <button
                    type="button"
                    onClick={handleEditPayment}
                    disabled={(() => {
                      if (user?.paymentChangeRequest?.submittedAt) {
                        const lastSubmit = user.paymentChangeRequest.submittedAt;
                        const daysSinceLastSubmit = Math.floor((Date.now() - lastSubmit.getTime()) / (1000 * 60 * 60 * 24));
                        return daysSinceLastSubmit < 60;
                      }
                      return false;
                    })()}
                    style={{
                      padding: '14px 32px',
                      background: (() => {
                        if (user?.paymentChangeRequest?.submittedAt) {
                          const lastSubmit = user.paymentChangeRequest.submittedAt;
                          const daysSinceLastSubmit = Math.floor((Date.now() - lastSubmit.getTime()) / (1000 * 60 * 60 * 24));
                          if (daysSinceLastSubmit < 60) {
                            return '#9ca3af';
                          }
                        }
                        return 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)';
                      })(),
                      border: 'none',
                      borderRadius: '12px',
                      color: '#ffffff',
                      fontSize: '15px',
                      fontWeight: '700',
                      cursor: (() => {
                        if (user?.paymentChangeRequest?.submittedAt) {
                          const lastSubmit = user.paymentChangeRequest.submittedAt;
                          const daysSinceLastSubmit = Math.floor((Date.now() - lastSubmit.getTime()) / (1000 * 60 * 60 * 24));
                          if (daysSinceLastSubmit < 60) {
                            return 'not-allowed';
                          }
                        }
                        return 'pointer';
                      })(),
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 16px rgba(238, 66, 56, 0.3)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                    onMouseEnter={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(238, 66, 56, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(238, 66, 56, 0.3)';
                    }}
                  >
                    {t('profile.payment.editCta')}
                  </button>
                </div>
              </div>
            )}

            {/* Payment Form for Editing */}
            {isEditingPayment && (
              <form onSubmit={handlePaymentSubmit}>
                {/* Payment Method Selection */}
                <div style={{ marginBottom: screenWidth < 768 ? '20px' : '24px' }}>
                  <label style={{
                    display: 'block',
                    color: '#495057',
                    fontSize: screenWidth < 768 ? '12px' : '13px',
                    fontWeight: '600',
                    marginBottom: screenWidth < 768 ? '10px' : '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {t('profile.payment.method')}
                  </label>
                  <div style={{
                    display: 'flex',
                    gap: screenWidth < 768 ? '10px' : '12px',
                    marginBottom: screenWidth < 768 ? '16px' : '20px'
                  }}>
                    <button
                      type="button"
                      onClick={() => handlePaymentInputChange('paymentMethod', 'upi')}
                      style={{
                        flex: 1,
                        padding: screenWidth < 768 ? '10px 12px' : '12px 16px',
                        borderRadius: '10px',
                        border: `2px solid ${paymentFormData.paymentMethod === 'upi' ? '#4ade80' : '#e9ecef'}`,
                        background: paymentFormData.paymentMethod === 'upi' ? '#f0fdf4' : '#ffffff',
                        color: paymentFormData.paymentMethod === 'upi' ? '#166534' : '#6c757d',
                        fontSize: screenWidth < 768 ? '13px' : '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      💳 {t('profile.payment.upiOption')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePaymentInputChange('paymentMethod', 'bank')}
                      style={{
                        flex: 1,
                        padding: screenWidth < 768 ? '10px 12px' : '12px 16px',
                        borderRadius: '10px',
                        border: `2px solid ${paymentFormData.paymentMethod === 'bank' ? '#4ade80' : '#e9ecef'}`,
                        background: paymentFormData.paymentMethod === 'bank' ? '#f0fdf4' : '#ffffff',
                        color: paymentFormData.paymentMethod === 'bank' ? '#166534' : '#6c757d',
                        fontSize: screenWidth < 768 ? '13px' : '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      🏦 {t('profile.payment.bankOption')}
                    </button>
                  </div>
                </div>

                {/* UPI Section */}
                {paymentFormData.paymentMethod === 'upi' && (
                  <div style={{ marginBottom: screenWidth < 768 ? '20px' : '24px' }}>
                    <div style={{
                      background: '#f8f9fa',
                      borderRadius: screenWidth < 768 ? '14px' : '16px',
                      padding: screenWidth < 768 ? '16px' : '20px',
                      border: '1px solid #e9ecef'
                    }}>
                      <div style={{
                        color: '#495057',
                        fontSize: screenWidth < 768 ? '13px' : '14px',
                        fontWeight: '600',
                        marginBottom: screenWidth < 768 ? '12px' : '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: screenWidth < 768 ? '6px' : '8px'
                      }}>
                        💳 {t('profile.payment.upiDetails')}
                      </div>

                      <div style={{ marginBottom: screenWidth < 768 ? '12px' : '16px' }}>
                        <label style={{
                          display: 'block',
                          color: '#6c757d',
                          fontSize: screenWidth < 768 ? '11px' : '12px',
                          fontWeight: '600',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('profile.payment.upiIdLabel')}
                        </label>
                        <input
                          type="text"
                          value={paymentFormData.upiId}
                          onChange={(e) => handlePaymentInputChange('upiId', e.target.value)}
                          style={{
                            width: '100%',
                            padding: screenWidth < 768 ? '10px 12px' : '12px 14px',
                            borderRadius: '10px',
                            border: '2px solid #e9ecef',
                            background: '#ffffff',
                            color: '#1a1a1a',
                            fontSize: screenWidth < 768 ? '13px' : '14px',
                            outline: 'none',
                            transition: 'all 0.2s ease',
                            boxSizing: 'border-box'
                          }}
                          placeholder={t('profile.payment.upiIdPlaceholder')}
                          required={paymentFormData.paymentMethod === 'upi'}
                          onFocus={(e) => {
                            e.target.style.border = '2px solid #4ade80';
                            e.target.style.boxShadow = '0 0 0 3px rgba(74, 222, 128, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.border = '2px solid #e9ecef';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </div>

                      {/* UPI Proof Upload */}
                      <div style={{ marginBottom: '0px' }}>
                        <label style={{
                          display: 'block',
                          color: '#6c757d',
                          fontSize: screenWidth < 768 ? '11px' : '12px',
                          fontWeight: '600',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('profile.payment.upiProofLabel')} {user?.upiDocUrl ? t('profile.payment.upiProofUploaded') : '*'}
                        </label>
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={(e) => {
                            handlePaymentInputChange('upiDocFile', e.target.files?.[0] || null);
                            setShowUpiProofWarning(false);
                          }}
                          style={{
                            fontSize: screenWidth < 768 ? '13px' : '14px'
                          }}
                        />
                        {showUpiProofWarning && (
                          <div style={{
                            marginTop: screenWidth < 768 ? '6px' : '8px',
                            padding: screenWidth < 768 ? '6px 10px' : '8px 12px',
                            background: '#fee2e2',
                            border: '1px solid #fca5a5',
                            borderRadius: '8px',
                            fontSize: screenWidth < 768 ? '10px' : '11px',
                            color: '#991b1b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: screenWidth < 768 ? '4px' : '6px',
                            fontWeight: '600'
                          }}>
                            <span>❌</span>
                            <span>Required: Upload UPI proof (JPG/PNG only)</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Bank Account Section */}
                {paymentFormData.paymentMethod === 'bank' && (
                  <div style={{ marginBottom: screenWidth < 768 ? '20px' : '24px' }}>
                    <div style={{
                      background: '#f8f9fa',
                      borderRadius: screenWidth < 768 ? '14px' : '16px',
                      padding: screenWidth < 768 ? '16px' : '20px',
                      border: '1px solid #e9ecef'
                    }}>
                      <div style={{
                        color: '#495057',
                        fontSize: screenWidth < 768 ? '13px' : '14px',
                        fontWeight: '600',
                        marginBottom: screenWidth < 768 ? '12px' : '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: screenWidth < 768 ? '6px' : '8px'
                      }}>
                        🏦 {t('profile.payment.bankDetails')}
                      </div>

                      {/* Account Holder Name */}
                      <div style={{ marginBottom: screenWidth < 768 ? '12px' : '16px' }}>
                        <label style={{
                          display: 'block',
                          color: '#6c757d',
                          fontSize: screenWidth < 768 ? '11px' : '12px',
                          fontWeight: '600',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('profile.payment.accountHolder')}
                        </label>
                        <input
                          type="text"
                          value={paymentFormData.accountHolderName}
                          onChange={(e) => handlePaymentInputChange('accountHolderName', e.target.value)}
                          style={{
                            width: '100%',
                            padding: screenWidth < 768 ? '10px 12px' : '12px 14px',
                            borderRadius: '10px',
                            border: '2px solid #e9ecef',
                            background: '#ffffff',
                            color: '#1a1a1a',
                            fontSize: screenWidth < 768 ? '13px' : '14px',
                            outline: 'none',
                            transition: 'all 0.2s ease',
                            boxSizing: 'border-box'
                          }}
                          placeholder={t('profile.payment.accountHolder')}
                          required={paymentFormData.paymentMethod === 'bank'}
                          onFocus={(e) => {
                            e.target.style.border = '2px solid #4ade80';
                            e.target.style.boxShadow = '0 0 0 3px rgba(74, 222, 128, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.border = '2px solid #e9ecef';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </div>

                      {/* Bank Name */}
                      <div style={{ marginBottom: screenWidth < 768 ? '12px' : '16px' }}>
                        <label style={{
                          display: 'block',
                          color: '#6c757d',
                          fontSize: screenWidth < 768 ? '11px' : '12px',
                          fontWeight: '600',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('profile.payment.bankName')}
                        </label>
                        <input
                          type="text"
                          value={paymentFormData.bankName}
                          onChange={(e) => handlePaymentInputChange('bankName', e.target.value)}
                          style={{
                            width: '100%',
                            padding: screenWidth < 768 ? '10px 12px' : '12px 14px',
                            borderRadius: '10px',
                            border: '2px solid #e9ecef',
                            background: '#ffffff',
                            color: '#1a1a1a',
                            fontSize: screenWidth < 768 ? '13px' : '14px',
                            outline: 'none',
                            transition: 'all 0.2s ease',
                            boxSizing: 'border-box'
                          }}
                          placeholder={t('profile.payment.bankName')}
                          required={paymentFormData.paymentMethod === 'bank'}
                          onFocus={(e) => {
                            e.target.style.border = '2px solid #4ade80';
                            e.target.style.boxShadow = '0 0 0 3px rgba(74, 222, 128, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.border = '2px solid #e9ecef';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </div>

                      {/* Account Number */}
                      <div style={{ marginBottom: screenWidth < 768 ? '12px' : '16px' }}>
                        <label style={{
                          display: 'block',
                          color: '#6c757d',
                          fontSize: screenWidth < 768 ? '11px' : '12px',
                          fontWeight: '600',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('profile.payment.accountNumber')}
                        </label>
                        <input
                          type="text"
                          value={paymentFormData.accountNumber}
                          onChange={(e) => handlePaymentInputChange('accountNumber', e.target.value)}
                          style={{
                            width: '100%',
                            padding: screenWidth < 768 ? '10px 12px' : '12px 14px',
                            borderRadius: '10px',
                            border: '2px solid #e9ecef',
                            background: '#ffffff',
                            color: '#1a1a1a',
                            fontSize: screenWidth < 768 ? '13px' : '14px',
                            outline: 'none',
                            transition: 'all 0.2s ease',
                            boxSizing: 'border-box'
                          }}
                          placeholder={t('profile.payment.accountNumber')}
                          required={paymentFormData.paymentMethod === 'bank'}
                          onFocus={(e) => {
                            e.target.style.border = '2px solid #4ade80';
                            e.target.style.boxShadow = '0 0 0 3px rgba(74, 222, 128, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.border = '2px solid #e9ecef';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </div>

                      {/* Bank Document Upload */}
                      <div style={{ marginBottom: screenWidth < 768 ? '12px' : '16px' }}>
                        <label style={{
                          display: 'block',
                          color: '#6c757d',
                          fontSize: screenWidth < 768 ? '11px' : '12px',
                          fontWeight: '600',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('profile.payment.bankProofLabel')} {user?.bankDocUrl ? t('profile.payment.bankProofUploaded') : '*'}
                        </label>
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={(e) => {
                            handlePaymentInputChange('bankDocFile', e.target.files?.[0] || null);
                            setShowBankProofWarning(false);
                          }}
                          style={{
                            fontSize: screenWidth < 768 ? '13px' : '14px'
                          }}
                        />
                        {showBankProofWarning && (
                          <div style={{
                            marginTop: screenWidth < 768 ? '6px' : '8px',
                            padding: screenWidth < 768 ? '6px 10px' : '8px 12px',
                            background: '#fee2e2',
                            border: '1px solid #fca5a5',
                            borderRadius: '8px',
                            fontSize: screenWidth < 768 ? '10px' : '11px',
                            color: '#991b1b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: screenWidth < 768 ? '4px' : '6px',
                            fontWeight: '600'
                          }}>
                            <span>❌</span>
                            <span>{t('profile.payment.warningBankProof')}</span>
                          </div>
                        )}
                      </div>

                      {/* Confirm Account Number */}
                      <div style={{ marginBottom: screenWidth < 768 ? '12px' : '16px' }}>
                        <label style={{
                          display: 'block',
                          color: '#6c757d',
                          fontSize: screenWidth < 768 ? '11px' : '12px',
                          fontWeight: '600',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('profile.payment.reEnterAccountNumber')}
                        </label>
                        <input
                          type="text"
                          value={paymentFormData.confirmAccountNumber}
                          onChange={(e) => handlePaymentInputChange('confirmAccountNumber', e.target.value)}
                          style={{
                            width: '100%',
                            padding: screenWidth < 768 ? '10px 12px' : '12px 14px',
                            borderRadius: '10px',
                            border: paymentFormData.confirmAccountNumber && paymentFormData.confirmAccountNumber !== paymentFormData.accountNumber ? '2px solid #ef4444' : '2px solid #e9ecef',
                            background: '#ffffff',
                            color: '#1a1a1a',
                            fontSize: screenWidth < 768 ? '13px' : '14px',
                            outline: 'none',
                            transition: 'all 0.2s ease',
                            boxSizing: 'border-box'
                          }}
                          placeholder={t('profile.payment.reEnterAccountNumber')}
                          required={paymentFormData.paymentMethod === 'bank'}
                          onFocus={(e) => {
                            e.target.style.border = '2px solid #4ade80';
                            e.target.style.boxShadow = '0 0 0 3px rgba(74, 222, 128, 0.1)';
                          }}
                          onBlur={(e) => {
                            if (paymentFormData.confirmAccountNumber && paymentFormData.confirmAccountNumber !== paymentFormData.accountNumber) {
                              e.target.style.border = '2px solid #ef4444';
                              e.target.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                            } else {
                              e.target.style.border = '2px solid #e9ecef';
                              e.target.style.boxShadow = 'none';
                            }
                          }}
                        />
                        {paymentFormData.confirmAccountNumber && paymentFormData.confirmAccountNumber !== paymentFormData.accountNumber && (
                          <div style={{
                            color: '#ef4444',
                            fontSize: screenWidth < 768 ? '11px' : '12px',
                            marginTop: '4px',
                            fontWeight: '500'
                          }}>
                            {t('profile.payment.accountMismatch')}
                          </div>
                        )}
                      </div>

                      {/* IFSC Code */}
                      <div style={{ marginBottom: '0px' }}>
                        <label style={{
                          display: 'block',
                          color: '#6c757d',
                          fontSize: screenWidth < 768 ? '11px' : '12px',
                          fontWeight: '600',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('profile.payment.ifsc')}
                        </label>
                        <input
                          type="text"
                          value={paymentFormData.ifscCode}
                          onChange={(e) => handlePaymentInputChange('ifscCode', e.target.value)}
                          style={{
                            width: '100%',
                            padding: screenWidth < 768 ? '10px 12px' : '12px 14px',
                            borderRadius: '10px',
                            border: '2px solid #e9ecef',
                            background: '#ffffff',
                            color: '#1a1a1a',
                            fontSize: screenWidth < 768 ? '13px' : '14px',
                            outline: 'none',
                            transition: 'all 0.2s ease',
                            boxSizing: 'border-box'
                          }}
                          placeholder={t('profile.payment.ifsc')}
                          required={paymentFormData.paymentMethod === 'bank'}
                          onFocus={(e) => {
                            e.target.style.border = '2px solid #4ade80';
                            e.target.style.boxShadow = '0 0 0 3px rgba(74, 222, 128, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.border = '2px solid #e9ecef';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons for Edit Mode */}
                <div style={{
                  display: 'flex',
                  gap: screenWidth < 768 ? '10px' : '12px',
                  justifyContent: 'flex-end',
                  flexDirection: screenWidth < 768 ? 'column' : 'row'
                }}>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    disabled={paymentLoading}
                    style={{
                      padding: screenWidth < 768 ? '12px 20px' : '12px 24px',
                      background: '#f8f9fa',
                      border: '2px solid #e9ecef',
                      borderRadius: '10px',
                      color: '#6c757d',
                      fontSize: screenWidth < 768 ? '13px' : '14px',
                      fontWeight: '600',
                      cursor: paymentLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: paymentLoading ? 0.6 : 1,
                      width: screenWidth < 768 ? '100%' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      if (!paymentLoading) {
                        e.currentTarget.style.background = '#e9ecef';
                        e.currentTarget.style.color = '#495057';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!paymentLoading) {
                        e.currentTarget.style.background = '#f8f9fa';
                        e.currentTarget.style.color = '#6c757d';
                      }
                    }}
                  >
                    {t('profile.payment.cancelCta')}
                  </button>

                  <button
                    type="submit"
                    disabled={paymentLoading}
                    style={{
                      padding: screenWidth < 768 ? '12px 20px' : '12px 24px',
                      background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#ffffff',
                      fontSize: screenWidth < 768 ? '13px' : '14px',
                      fontWeight: '600',
                      cursor: paymentLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 12px rgba(74, 222, 128, 0.3)',
                      opacity: paymentLoading ? 0.7 : 1,
                      width: screenWidth < 768 ? '100%' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      if (!paymentLoading) {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(74, 222, 128, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!paymentLoading) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(74, 222, 128, 0.3)';
                      }
                    }}
                  >
                    {paymentLoading ? t('profile.payment.submitting') : t('profile.payment.submitCta')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditProfileModal && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowEditProfileModal(false);
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(8px)',
            padding: screenWidth < 768 ? '16px 16px calc(16px + 80px) 16px' : '20px 20px calc(20px + 80px) 20px',
            overflowY: 'auto',
            paddingTop: screenWidth < 768 ? '16px' : '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '24px',
              padding: '0px',
              maxWidth: screenWidth < 768 ? '100%' : screenWidth < 1024 ? '700px' : '850px',
              width: '100%',
              maxHeight: screenWidth < 768 ? 'calc(100vh - 120px)' : 'calc(100vh - 140px)',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              position: 'relative',
              marginTop: screenWidth < 768 ? '0' : '0'
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowEditProfileModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                color: '#6c757d',
                fontSize: '18px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e9ecef';
                e.currentTarget.style.color = '#495057';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f8f9fa';
                e.currentTarget.style.color = '#6c757d';
              }}
            >
              ✕
            </button>

            {/* Header (match registration form) */}
            <div style={{
              background: 'linear-gradient(135deg, #ee4238 0%, #af201c 100%)',
              borderTopLeftRadius: '24px',
              borderTopRightRadius: '24px',
              paddingTop: '40px', // ~pt-10
              paddingBottom: '24px', // ~pb-6
              paddingLeft: '24px',
              paddingRight: '24px',
              textAlign: 'center'
            }}>
              <h2 style={{ color: '#ffffff', fontSize: '22px', fontWeight: 900, margin: 0 }}>{t('profile.editProfile.title')}</h2>
              <p style={{ color: 'rgba(255,255,255,0.9)', marginTop: '12px', fontSize: '13px' }}>{t('profile.editProfile.subtitle')}</p>
            </div>

            {/* Edit Form (white rounded card like registration) - Read-only view */}
            <form onSubmit={(e) => { e.preventDefault(); }} style={{
              background: '#ffffff',
              borderBottomLeftRadius: '24px',
              borderBottomRightRadius: '24px',
              padding: '24px',
              marginTop: 0
            }}>
              {/* Account Type - Fixed (match registration) */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#111827', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{t('profile.editProfile.accountType')}</label>
                <div style={{
                  width: '100%', borderRadius: '16px', border: '1px solid #e5e7eb', background: '#f3f4f6',
                  padding: '12px 16px', color: '#4b5563', fontSize: '15px'
                }}>
                  Painter
                </div>
              </div>

              {/* Mobile Number - read-only below Account Type */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: '#111827', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{t('profile.editProfile.mobileNumber')}</label>
                <div style={{
                  width: '100%', borderRadius: '16px', border: '1px solid #e5e7eb', background: '#f3f4f6',
                  padding: '12px 16px', color: '#111827', fontSize: '15px', fontWeight: 700, letterSpacing: '0.02em'
                }}>
                  {editFormData.phone || user?.phone || ''}
                </div>
              </div>
              {/* First Name & Last Name - like registration */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div>
                  <label style={{
                    display: 'block', color: '#495057', fontSize: '13px', fontWeight: '600', marginBottom: '6px',
                    textTransform: 'uppercase', letterSpacing: '0.5px'
                  }}>{t('profile.editProfile.firstName')}</label>
                  <input
                    type="text"
                    value={editFirstName}
                    disabled={true}
                    readOnly={true}
                    style={{
                      width: '100%', padding: '14px 16px', borderRadius: '12px', border: '2px solid #e9ecef',
                      background: '#f3f4f6', color: '#4b5563', fontSize: '15px', outline: 'none', transition: 'all 0.2s ease', boxSizing: 'border-box', cursor: 'not-allowed'
                    }}
                    placeholder={t('profile.editProfile.firstName')}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block', color: '#495057', fontSize: '13px', fontWeight: '600', marginBottom: '6px',
                    textTransform: 'uppercase', letterSpacing: '0.5px'
                  }}>{t('profile.editProfile.lastName')}</label>
                  <input
                    type="text"
                    value={editLastName}
                    disabled={true}
                    readOnly={true}
                    style={{
                      width: '100%', padding: '14px 16px', borderRadius: '12px', border: '2px solid #e9ecef',
                      background: '#f3f4f6', color: '#4b5563', fontSize: '15px', outline: 'none', transition: 'all 0.2s ease', boxSizing: 'border-box', cursor: 'not-allowed'
                    }}
                    placeholder={t('profile.editProfile.lastName')}
                  />
                </div>
              </div>

              {/* Phone is read-only and hidden here to mirror registration order */}

              {/* State & District (match registration) */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', color: '#111827', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{t('profile.editProfile.state')}</label>
                  <select
                    value={editFormData.state}
                    disabled={true}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e5e7eb', background: '#f3f4f6', color: '#4b5563', cursor: 'not-allowed' }}
                  >
                    <option value="">{t('profile.editProfile.selectState')}</option>
                    {getUniqueStates().map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', color: '#111827', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{t('profile.editProfile.district')}</label>
                  <select
                    value={editFormData.district}
                    disabled={true}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e5e7eb', background: '#f3f4f6', color: '#4b5563', cursor: 'not-allowed' }}
                  >
                    <option value="">{t('profile.editProfile.selectDistrict')}</option>
                    {editFormData.state && getDistrictsForState(editFormData.state).map(district => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* City & Locality (match registration) */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', color: '#111827', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{t('profile.editProfile.city')} *</label>
                  <input type="text" value={editFormData.city} disabled={true} readOnly={true}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e5e7eb', background: '#f3f4f6', color: '#4b5563', cursor: 'not-allowed' }}
                    placeholder={t('profile.editProfile.cityPlaceholder')} />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#111827', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{t('profile.editProfile.locality')} *</label>
                  <input type="text" value={editFormData.address || ''} disabled={true} readOnly={true}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e5e7eb', background: '#f3f4f6', color: '#4b5563', cursor: 'not-allowed' }}
                    placeholder={t('profile.editProfile.localityPlaceholder')} />
                </div>
              </div>

              {/* Age (comes after PIN like registration) */}
              <div style={{ marginTop: '16px' }}>
                <label style={{
                  display: 'block',
                  color: '#495057',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {t('profile.editProfile.age')}
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={(editFormData.age as any) || ''}
                  disabled={true}
                  readOnly={true}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '12px',
                    border: '2px solid #e9ecef',
                    background: '#f3f4f6',
                    color: '#4b5563',
                    fontSize: '15px',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box',
                    cursor: 'not-allowed'
                  }}
                  placeholder={t('profile.editProfile.agePlaceholder')}
                />
              </div>

              {/* PIN Code (single, outside location section to avoid duplication) */}
              <div style={{ marginTop: 0 }}>
                <label style={{ display: 'block', color: '#111827', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{t('profile.editProfile.pinCode')} *</label>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={editFormData.pinCode}
                  disabled={true}
                  readOnly={true}
                  style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e5e7eb', background: '#f3f4f6', color: '#4b5563', cursor: 'not-allowed' }}
                  placeholder={t('profile.editProfile.pinPlaceholder')}
                />
              </div>

              {/* Action Button - Hidden (read-only view) */}
              <div style={{ paddingTop: '16px', display: 'none' }}>
                <button
                  type="submit"
                  disabled={true}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
                    border: 'none',
                    borderRadius: '16px',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: 900,
                    cursor: 'not-allowed',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 10px 20px rgba(238, 66, 56, 0.25)',
                    opacity: 0.7
                  }}
                >
                  {editLoading ? t('profile.editProfile.saving') : t('profile.editProfile.saveCta')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* KYC Modal */}
      {showKycModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
          backdropFilter: 'blur(8px)', padding: '20px'
        }}>
          <div style={{ background: '#ffffff', borderRadius: '24px', maxWidth: '480px', width: '100%', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #ee4238, #af201c)', padding: '24px', textAlign: 'center' }}>
              <h3 style={{ color: '#fff', margin: 0, fontSize: '20px', fontWeight: 800 }}>KYC Details</h3>
              <p style={{ color: 'rgba(255,255,255,0.9)', margin: '8px 0 0 0', fontSize: '13px' }}>{t('profile.kyc.mandatoryNote')}</p>
            </div>
            {/* Body */}
            {/* If approved or submitted, show status card; if rejected, allow resubmission with form */}
            {user?.kyc && (user.kyc.status === 'approved' || user.kyc.status === 'submitted') ? (
              <div style={{ padding: '20px' }}>
                <div style={{
                  display: 'inline-block', padding: '6px 10px', borderRadius: '9999px', fontWeight: 700, fontSize: '12px', marginBottom: '12px',
                  background: user.kyc.status === 'approved' ? '#ecfdf5' : (user.kyc.status as any) === 'rejected' ? '#fef2f2' : '#eff6ff',
                  color: user.kyc.status === 'approved' ? '#065f46' : (user.kyc.status as any) === 'rejected' ? '#991b1b' : '#1e40af',
                  border: '1px solid rgba(0,0,0,0.05)'
                }}>
                  {user.kyc.status.toUpperCase()}
                </div>
                <div style={{ background: '#f9fafb', borderRadius: '16px', border: '1px solid #e5e7eb', padding: '16px' }}>
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ color: '#6b7280', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('profile.kyc.realName')}</div>
                    <div style={{ color: '#111827', fontSize: '15px', fontWeight: 700 }}>{user.kyc.realName || '-'}</div>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ color: '#6b7280', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('profile.kyc.aadhaarNumber')}</div>
                    <div style={{ color: '#111827', fontSize: '15px', fontWeight: 700 }}>{(user.kyc.aadhaarNumber || '').replace(/\d(?=\d{4})/g, '•')}</div>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ color: '#6b7280', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('profile.kyc.panNumber')}</div>
                    <div style={{ color: '#111827', fontSize: '15px', fontWeight: 700 }}>{user.kyc.panNumber || '-'}</div>
                  </div>
                  <div style={{ marginTop: '6px', color: '#6b7280', fontSize: '12px' }}>
                    {t('profile.kyc.submittedOn')} {user.kyc.submittedAt ? new Date(user.kyc.submittedAt as any).toLocaleString() : '-'}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                  <button type="button" onClick={() => setShowKycModal(false)}
                    style={{ padding: '12px 16px', background: '#fff5f4', border: '1px solid #ffd1cb', color: '#af201c', borderRadius: '12px', fontWeight: 700 }}>
                    {t('profile.kyc.closeCta')}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={async (e) => { await handleSubmitKyc(e); /* Optimistic UI: mark as submitted immediately */ try { markKycStatusLocally({ status: 'submitted', submittedAt: new Date() }); } catch { } }} style={{ padding: '20px' }}>
                {user?.kyc?.status === 'rejected' && (
                  <div style={{ marginBottom: '12px', color: '#991b1b', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '12px', padding: '10px', fontSize: '13px' }}>
                    {t('profile.kyc.rejectedNote')} {user.kyc.rejectReason ? `${t('profile.kyc.reasonPrefix')} ${user.kyc.rejectReason}` : ''} Please correct and re-submit.
                  </div>
                )}
                {/* Real Name */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', color: '#111827', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{t('profile.kyc.realName')}</label>
                  <input type="text" value={kycRealName} onChange={(e) => setKycRealName(e.target.value)}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e5e7eb', background: '#fff' }} required />
                </div>
                {/* Aadhaar Number */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', color: '#111827', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{t('profile.kyc.aadhaarNumber')}</label>
                  <input type="tel" inputMode="numeric" pattern="[0-9]*" value={kycAadhaarNumber}
                    onChange={(e) => setKycAadhaarNumber(e.target.value.replace(/\D/g, '').slice(0, 12))}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e5e7eb', background: '#fff' }} required />
                </div>
                {/* Aadhaar Photo */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', color: '#111827', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{t('profile.kyc.aadhaarPhoto')}</label>
                  <input type="file" accept="image/*" onChange={(e) => onSelectKycImage(e, 'aadhaar')} />
                </div>
                {/* PAN Number */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', color: '#111827', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{t('profile.kyc.panNumber')}</label>
                  <input type="text" value={kycPanNumber} onChange={(e) => setKycPanNumber(e.target.value.toUpperCase().slice(0, 10))}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e5e7eb', background: '#fff' }} required />
                </div>
                {/* PAN Photo */}
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ display: 'block', color: '#111827', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{t('profile.kyc.panPhoto')}</label>
                  <input type="file" accept="image/*" onChange={(e) => onSelectKycImage(e, 'pan')} />
                </div>
                {/* Actions */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                  <button type="button" onClick={() => setShowKycModal(false)}
                    style={{ flex: 1, padding: '12px 16px', background: '#fff5f4', border: '1px solid #ffd1cb', color: '#af201c', borderRadius: '12px', fontWeight: 700 }}>
                    {t('profile.kyc.cancelCta')}
                  </button>
                  <button type="submit" disabled={kycSaving}
                    style={{ flex: 1, padding: '12px 16px', background: 'linear-gradient(135deg, #ee4238, #af201c)', border: 'none', color: '#fff', borderRadius: '12px', fontWeight: 800 }}>
                    {kycSaving ? t('profile.kyc.submitting') : t('profile.kyc.submitCta')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* App Preferences Modal */}
      {showAppPrefModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 12000,
            padding: '16px'
          }}
          onClick={() => setShowAppPrefModal(false)}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '420px',
              background: '#fff',
              borderRadius: '20px',
              boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
              overflow: 'hidden',
              border: '1px solid #f3f4f6'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '16px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '16px', color: '#111827' }}>{t('profile.appPreferences')}</div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>{t('profile.appPreferencesSubtitle')}</div>
              </div>
              <button
                onClick={() => setShowAppPrefModal(false)}
                style={{ border: 'none', background: 'transparent', fontSize: '18px', color: '#9ca3af', cursor: 'pointer' }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div style={{ padding: '14px 18px', display: 'grid', gap: '10px' }}>
              {languageOptions.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleSelectLanguage(lang.code)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 14px',
                    borderRadius: '12px',
                    border: appLanguage === lang.code ? '1px solid #ee4238' : '1px solid #e5e7eb',
                    background: appLanguage === lang.code ? 'rgba(238,66,56,0.08)' : '#fff',
                    color: '#111827',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span>{lang.label}</span>
                  {appLanguage === lang.code && (
                    <span style={{ fontSize: '12px', color: '#ee4238', fontWeight: 800 }}>{t('common.selected')}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Dealer Code Modal */}
      <DealerCodeModal
        isOpen={showDealerModal}
        onClose={() => {
          setShowDealerModal(false);
          // Don't refresh user data on close if just viewing info
          // Only refresh if we just associated (handled in onSuccess)
        }}
        onSuccess={() => {
          // Close modal first
          setShowDealerModal(false);
          // Then refresh user data after a short delay to ensure Firestore has updated
          if (refreshUserData) {
            setTimeout(() => {
              refreshUserData();
            }, 1000);
          }
        }}
        existingDealerInfo={dealerInfo}
        isReadOnly={!!user?.dealerCode}
      />
    </div>
  );
});

export default Profile; 